import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { LogOut, LayoutDashboard, MapPin, Calendar, FileText, Users, Settings, Plus, Pencil, Trash2, ExternalLink, Eye, Info, Upload, Link2, Check, ImageIcon, Sun, Moon, Monitor, Globe, XCircle, Trash, Menu, X, LogIn, Armchair, Utensils, Plug, Copy, RefreshCw, Shield, Code, Ban, Clock, Bot, BarChart3, Key, AlertCircle, Cpu, Mail, TrendingUp, Download, Instagram, MessageCircle, Megaphone } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { formatDateStr } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import IconPicker, { IconDisplay } from "@/components/IconPicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, LabelList } from "recharts";

const TABS = [
  { id: "reservations", label: "Reservas", icon: LayoutDashboard },
  { id: "sedes", label: "Sedes", icon: MapPin },
  { id: "zone-types", label: "Tipos de Zona", icon: Armchair },
  { id: "reservation-purposes", label: "Motivos de Reserva", icon: Calendar },
  { id: "conditions", label: "Condiciones", icon: FileText },
  { id: "blocked-dates", label: "Fechas bloqueadas", icon: Ban },
  { id: "users", label: "Usuarios", icon: Users },
];

export default function AdminPanel() {
  const [tab, setTab] = useState("reservations");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, impersonating } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/"); };
  const selectTab = (id) => { setTab(id); setSidebarOpen(false); };

  return (
    <div className={`min-h-screen bg-background flex ${impersonating ? 'pt-10' : ''}`} data-testid="admin-panel">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div className={`fixed lg:static inset-y-0 left-0 z-40 w-56 border-r border-border bg-card flex flex-col flex-shrink-0 h-screen transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${impersonating ? 'top-10 h-[calc(100vh-2.5rem)]' : ''}`}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary">
                <Utensils className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm text-foreground">Reservas</div>
                <div className="text-xs text-muted-foreground">Admin</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="lg:hidden h-7 w-7 p-0" onClick={() => setSidebarOpen(false)}><X className="w-4 h-4" /></Button>
          </div>
        </div>
        <nav className="flex-1 py-1 px-2 overflow-y-auto space-y-0.5">
          {TABS.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`}
              className={`admin-sidebar-item w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg ${tab === t.id ? 'active' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => selectTab(t.id)}>
              <t.icon className="w-4 h-4 flex-shrink-0" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-border px-2 py-3 space-y-0.5">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 mb-2 font-body">Configuraci&oacute;n</p>
          <button data-testid="admin-tab-integrations"
            className={`admin-sidebar-item w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg ${tab === 'integrations' ? 'active' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => selectTab('integrations')}>
            <Plug className="w-4 h-4 flex-shrink-0" /> Integraciones
          </button>
          <button data-testid="admin-tab-resources"
            className={`admin-sidebar-item w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg ${tab === 'resources' ? 'active' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => selectTab('resources')}>
            <Cpu className="w-4 h-4 flex-shrink-0" /> Recursos
          </button>
          <button data-testid="admin-tab-settings"
            className={`admin-sidebar-item w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg ${tab === 'settings' ? 'active' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => selectTab('settings')}>
            <Settings className="w-4 h-4 flex-shrink-0" /> Ajustes
          </button>
          <p className="text-xs text-muted-foreground px-3 pt-2 truncate">{user?.email}</p>
          <div className="px-1 pt-1">
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-red-500 hover:bg-red-500/10" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesi&oacute;n
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 p-3 border-b border-border bg-card">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
          <span className="text-sm font-semibold">{TABS.find(t => t.id === tab)?.label || 'Configuraci\u00f3n'}</span>
        </div>
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          {tab === "sedes" && <SedesTab />}
          {tab === "zone-types" && <ZoneTypesTab />}
          {tab === "reservation-purposes" && <ReservationPurposesTab />}
          {tab === "conditions" && <ConditionsTab />}
          {tab === "blocked-dates" && <BlockedDatesTab />}
          {tab === "reservations" && <ReservationsTab />}
          {tab === "users" && <UsersTab />}
          {tab === "resources" && <ResourcesTab />}
          {tab === "integrations" && <IntegrationsTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}

// ==================== INTEGRATIONS TAB (wrapper: Widget + Chatbot API) ====================
function IntegrationsTab() {
  const [section, setSection] = useState("widget");
  return (
    <div data-testid="integrations-tab">
      <h2 className="text-2xl font-bold mb-6">Integraciones</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        <Button data-testid="integrations-section-widget" size="sm" variant={section === 'widget' ? 'default' : 'outline'} className={section === 'widget' ? 'bg-primary text-primary-foreground' : ''} onClick={() => setSection('widget')}>
          <Code className="w-3.5 h-3.5 mr-1.5" /> Widget Web
        </Button>
        <Button data-testid="integrations-section-chatbot" size="sm" variant={section === 'chatbot' ? 'default' : 'outline'} className={section === 'chatbot' ? 'bg-primary text-primary-foreground' : ''} onClick={() => setSection('chatbot')}>
          <Bot className="w-3.5 h-3.5 mr-1.5" /> Chatbot API
        </Button>
      </div>
      {section === 'widget' && <WidgetIntegrationSection />}
      {section === 'chatbot' && <ChatbotIntegrationSection />}
    </div>
  );
}

// ==================== WIDGET SECTION (legacy content, unchanged) ====================
function WidgetIntegrationSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOrigin, setNewOrigin] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(() => {
    API.get("/widget/config").then(r => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado al portapapeles")).catch(() => toast.error("Error al copiar"));
  };

  const regenerateKeys = async () => {
    if (!window.confirm("Las claves actuales dejar\u00e1n de funcionar inmediatamente. \u00bfContinuar?")) return;
    setRegenerating(true);
    try {
      const { data } = await API.post("/widget/regenerate-keys");
      setConfig(data);
      toast.success("Claves regeneradas");
    } catch { toast.error("Error al regenerar"); }
    finally { setRegenerating(false); }
  };

  const addOrigin = async () => {
    const origin = newOrigin.trim().replace(/\/+$/, "");
    if (!origin) return;
    try {
      new URL(origin);
    } catch {
      toast.error("URL inv\u00e1lida. Usa formato: https://ejemplo.com");
      return;
    }
    const origins = [...(config?.allowed_origins || []), origin];
    try {
      const { data } = await API.put("/widget/config", { allowed_origins: origins });
      setConfig(data);
      setNewOrigin("");
      toast.success("Origen agregado");
    } catch { toast.error("Error"); }
  };

  const removeOrigin = async (origin) => {
    const origins = (config?.allowed_origins || []).filter(o => o !== origin);
    try {
      const { data } = await API.put("/widget/config", { allowed_origins: origins });
      setConfig(data);
      toast.success("Origen eliminado");
    } catch { toast.error("Error"); }
  };

  const toggleEnabled = async () => {
    try {
      const { data } = await API.put("/widget/config", { enabled: !config?.enabled });
      setConfig(data);
      toast.success(data.enabled ? "Widget habilitado" : "Widget deshabilitado");
    } catch { toast.error("Error"); }
  };

  const frontendOrigin = window.location.origin;
  const embedCode = config ? `<div id="reserva-widget"></div>\n<script src="${frontendOrigin}/widget.js"><\/script>\n<script>\n  ReservaWidget.init({\n    publicKey: '${config.public_key}',\n    container: '#reserva-widget'\n  });\n<\/script>` : "";

  if (loading) return <p className="text-muted-foreground">Cargando...</p>;

  return (
    <div data-testid="widget-section">
      {/* Widget Status */}
      <Card className="bg-card border-border mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Code className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold font-heading">Widget de Reservas</h3>
                <p className="text-xs text-muted-foreground">Integra el formulario de reservas en tu sitio web</p>
              </div>
            </div>
            <button type="button" data-testid="widget-toggle" className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${config?.enabled ? 'bg-primary' : 'bg-secondary'}`} onClick={toggleEnabled}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* API Keys */}
          <div className="space-y-3 mb-6">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" /> Clave p&uacute;blica (public_key)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input readOnly value={config?.public_key || ""} className="bg-background border-border font-mono text-xs" data-testid="public-key-input" />
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(config?.public_key)} data-testid="copy-public-key"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={regenerateKeys} disabled={regenerating} data-testid="regenerate-keys-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating ? 'animate-spin' : ''}`} /> {regenerating ? "Regenerando..." : "Regenerar clave"}
            </Button>
          </div>

          {/* Allowed Origins */}
          <div className="mb-6">
            <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-2"><Globe className="w-3 h-3" /> Or&iacute;genes permitidos</Label>
            <p className="text-xs text-muted-foreground mb-3">Solo estos dominios podr&aacute;n usar el widget. Si la lista est&aacute; vac&iacute;a, cualquier origen puede usarlo.</p>
            <div className="space-y-2 mb-3">
              {(config?.allowed_origins || []).map(origin => (
                <div key={origin} className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2">
                  <span className="text-sm font-mono truncate">{origin}</span>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 hover:bg-red-500/10 h-7 w-7 p-0 flex-shrink-0" onClick={() => removeOrigin(origin)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              {(config?.allowed_origins || []).length === 0 && (
                <p className="text-xs text-amber-500 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">Sin restricciones de origen. Cualquier sitio web puede usar el widget.</p>
              )}
            </div>
            <div className="flex gap-2">
              <Input value={newOrigin} onChange={e => setNewOrigin(e.target.value)} placeholder="https://misitio.com" className="bg-background border-border text-sm" data-testid="new-origin-input" onKeyDown={e => e.key === "Enter" && addOrigin()} />
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0" onClick={addOrigin} data-testid="add-origin-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</Button>
            </div>
          </div>

          {/* Embed Code */}
          <div>
            <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-2"><Code className="w-3 h-3" /> C&oacute;digo de integraci&oacute;n</Label>
            <div className="relative">
              <pre className="bg-background border border-border rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-code">{embedCode}</pre>
              <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => copyToClipboard(embedCode)} data-testid="copy-embed-code"><Copy className="w-3 h-3 mr-1" /> Copiar</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== CHATBOT INTEGRATION SECTION ====================
function ChatbotIntegrationSection() {
  const [tab, setTab] = useState("configs");
  return (
    <div data-testid="chatbot-section">
      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="chatbot-tab-configs" size="sm" variant={tab === 'configs' ? 'secondary' : 'ghost'} onClick={() => setTab('configs')}>
          <Key className="w-3.5 h-3.5 mr-1.5" /> Claves API
        </Button>
        <Button data-testid="chatbot-tab-explorer" size="sm" variant={tab === 'explorer' ? 'secondary' : 'ghost'} onClick={() => setTab('explorer')}>
          <Code className="w-3.5 h-3.5 mr-1.5" /> Explorer
        </Button>
        <Button data-testid="chatbot-tab-logs" size="sm" variant={tab === 'logs' ? 'secondary' : 'ghost'} onClick={() => setTab('logs')}>
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Logs
        </Button>
        <Button data-testid="chatbot-tab-report" size="sm" variant={tab === 'report' ? 'secondary' : 'ghost'} onClick={() => setTab('report')}>
          <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Reportes
        </Button>
      </div>
      {tab === 'configs' && <ChatbotConfigs />}
      {tab === 'explorer' && <ChatbotExplorer />}
      {tab === 'logs' && <ChatbotLogs />}
      {tab === 'report' && <ChatbotReport />}
    </div>
  );
}

function ChatbotConfigs() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [revealedKey, setRevealedKey] = useState(null); // { id, name, api_key }

  const load = useCallback(() => {
    setLoading(true);
    API.get("/admin/chatbot-configs")
      .then(r => { setConfigs(r.data || []); })
      .catch(() => toast.error("Error al cargar configuraciones"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado")).catch(() => toast.error("Error al copiar"));
  };

  const createConfig = async () => {
    if (!newName.trim()) { toast.error("Nombre requerido"); return; }
    setCreating(true);
    try {
      const { data } = await API.post("/admin/chatbot-configs", { name: newName.trim() });
      setRevealedKey({ id: data.id, name: data.name, api_key: data.api_key });
      setNewName("");
      await load();
      toast.success("Clave creada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (cfg) => {
    try {
      await API.put(`/admin/chatbot-configs/${cfg.id}`, { active: !cfg.active });
      await load();
      toast.success(cfg.active ? "Desactivada" : "Activada");
    } catch { toast.error("Error"); }
  };

  const regenerate = async (cfg) => {
    if (!window.confirm(`La clave actual de "${cfg.name}" dejara de funcionar inmediatamente. \u00bfContinuar?`)) return;
    try {
      const { data } = await API.post(`/admin/chatbot-configs/${cfg.id}/regenerate`);
      setRevealedKey({ id: data.id, name: data.name, api_key: data.api_key });
      await load();
      toast.success("Clave regenerada");
    } catch { toast.error("Error al regenerar"); }
  };

  const deleteConfig = async (cfg) => {
    if (!window.confirm(`\u00bfEliminar "${cfg.name}"? Esta acci\u00f3n no se puede deshacer.`)) return;
    try {
      await API.delete(`/admin/chatbot-configs/${cfg.id}`);
      await load();
      toast.success("Eliminada");
    } catch { toast.error("Error"); }
  };

  return (
    <Card className="bg-card border-border" data-testid="chatbot-configs-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold font-heading">Claves API para Chatbots</h3>
            <p className="text-xs text-muted-foreground">Genera claves para que tus chatbots creen reservas v&iacute;a API REST.</p>
          </div>
        </div>

        {/* Create */}
        <div className="flex gap-2 mb-6">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre (ej: WhatsApp Bot)" className="bg-background border-border text-sm" data-testid="new-chatbot-name" onKeyDown={e => e.key === "Enter" && createConfig()} />
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0" onClick={createConfig} disabled={creating} data-testid="create-chatbot-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Crear clave</Button>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-muted-foreground text-sm">Cargando...</p>
        ) : configs.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-background border border-border rounded-lg p-4 text-center">No hay claves creadas. Crea una para integrar tu primer chatbot.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="chatbot-configs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Prefijo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Creada</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map(c => (
                  <TableRow key={c.id} data-testid={`chatbot-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="font-mono text-xs">{c.key_prefix}***</TableCell>
                    <TableCell>
                      <Badge variant={c.active ? "default" : "secondary"} className={c.active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}>
                        {c.active ? "Activa" : "Inactiva"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" title={c.active ? "Desactivar" : "Activar"} onClick={() => toggleActive(c)} data-testid={`toggle-${c.id}`}><Shield className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" title="Regenerar" onClick={() => regenerate(c)} data-testid={`regen-${c.id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 hover:bg-red-500/10" title="Eliminar" onClick={() => deleteConfig(c)} data-testid={`delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Reveal modal: shown after create/regenerate */}
        <Dialog open={!!revealedKey} onOpenChange={(open) => !open && setRevealedKey(null)}>
          <DialogContent className="max-w-lg" data-testid="reveal-key-dialog">
            <DialogHeader>
              <DialogTitle>Tu nueva clave API</DialogTitle>
              <DialogDescription>
                Esta es la <strong>\u00fanica vez</strong> que ver&aacute;s la clave completa. C&oacute;piala ahora y gu&aacute;rdala en un lugar seguro.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Por seguridad, la clave queda hasheada en la base de datos. Si la pierdes, deber&aacute;s regenerarla.
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Nombre</Label>
                <p className="text-sm font-medium">{revealedKey?.name}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Clave API completa</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={revealedKey?.api_key || ""} className="bg-background border-border font-mono text-xs" data-testid="revealed-key-input" />
                  <Button size="sm" variant="outline" onClick={() => copy(revealedKey?.api_key)} data-testid="copy-revealed-key"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Uso (header HTTP)</Label>
                <pre className="bg-background border border-border rounded-lg p-3 text-xs font-mono mt-1 whitespace-pre-wrap break-all">X-API-Key: {revealedKey?.api_key}</pre>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setRevealedKey(null)} data-testid="close-reveal-btn">Entendido, ya la copi&eacute;</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const MONTH_LABELS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

// Format a UTC ISO timestamp in the platform's configured timezone (default Bogot\u00e1).
// Backend `chatbot-report` and `chatbot-logs` responses include a `timezone` field;
// when absent we fall back to America/Bogota.
function formatInTz(isoString, tz, includeSeconds = false) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    return d.toLocaleString("es-CO", {
      timeZone: tz || "America/Bogota",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      ...(includeSeconds ? { second: "2-digit" } : {}),
      hour12: false,
    });
  } catch {
    return new Date(isoString).toLocaleString();
  }
}

function MonthlyChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter(p => p.dataKey !== "Total");
  const total = payload.find(p => p.dataKey === "Total")?.value ?? 0;
  return (
    <div className="rounded-lg border border-border bg-card p-2 text-xs shadow-lg" style={{ minWidth: 140 }}>
      <div className="flex items-center justify-between gap-3 pb-2 mb-2 border-b border-border">
        <span className="font-semibold">{label}</span>
        <span className="font-mono">
          <span className="text-muted-foreground">Total</span>{" "}
          <span className="text-[#c9a84c] font-bold text-sm">{total}</span>
        </span>
      </div>
      <div className="space-y-1">
        {items.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color }} />
              <span className="text-muted-foreground">{p.dataKey}</span>
            </span>
            <span className="font-mono">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function ChatbotReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [configId, setConfigId] = useState("all");
  const [configs, setConfigs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/admin/chatbot-configs").then(r => setConfigs(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { year };
    if (configId && configId !== "all") params.chatbot_config_id = configId;
    API.get("/admin/chatbot-report", { params })
      .then(r => setReport(r.data))
      .catch(() => toast.error("Error al cargar reporte"))
      .finally(() => setLoading(false));
  }, [year, configId]);
  useEffect(() => { load(); }, [load]);

  // Build a full-year dataset (12 months, zero-filled for absent months)
  const chartData = useMemo(() => {
    const map = new Map();
    (report?.monthly || []).forEach(m => map.set(m.month, m));
    return Array.from({ length: 12 }, (_, i) => {
      const idx = i + 1;
      const found = map.get(idx) || { success: 0, validation_error: 0, error: 0, corporate_redirect: 0, total: 0 };
      const success = found.success || 0;
      const validation = found.validation_error || 0;
      const errors = found.error || 0;
      const corp = found.corporate_redirect || 0;
      return {
        name: MONTH_LABELS[i],
        Exitosas: success,
        Validaciones: validation,
        Errores: errors,
        Corporativas: corp,
        Total: found.total || (success + validation + errors + corp),
      };
    });
  }, [report]);

  const t = report?.totals || { total: 0, success: 0, validation_error: 0, error: 0, corporate_redirect: 0, avg_response_time_ms: 0 };
  const successRate = t.total > 0 ? Math.round((t.success / t.total) * 100) : 0;
  const errorRate = t.total > 0 ? Math.round((t.error / t.total) * 100) : 0;
  const years = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur, cur - 1, cur - 2];
  }, []);

  return (
    <div className="space-y-4" data-testid="chatbot-report">
      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-xs text-muted-foreground mb-1 block">A&ntilde;o</Label>
            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
              <SelectTrigger className="bg-background border-border" data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Integraci&oacute;n</Label>
            <Select value={configId} onValueChange={setConfigId}>
              <SelectTrigger className="bg-background border-border" data-testid="report-config-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las claves</SelectItem>
                {configs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Badge variant="outline" className="text-[10px] self-end mb-[2px]" data-testid="report-timezone-badge">
            <Clock className="w-3 h-3 mr-1" /> Zona horaria: {report?.timezone || "America/Bogota"}
          </Badge>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-card border-border"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total peticiones</p>
          <p className="text-2xl font-bold mt-1" data-testid="metric-total">{t.total}</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Tasa de &eacute;xito</p>
          <p className="text-2xl font-bold text-emerald-500 mt-1" data-testid="metric-success-rate">{successRate}%</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Validaciones</p>
          <p className="text-2xl font-bold text-amber-500 mt-1" data-testid="metric-validations">{t.validation_error}</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Errores reales</p>
          <p className="text-2xl font-bold text-red-500 mt-1" data-testid="metric-real-errors">{t.error}<span className="text-sm font-normal text-muted-foreground"> ({errorRate}%)</span></p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Redirecciones corp.</p>
          <p className="text-2xl font-bold text-primary mt-1" data-testid="metric-corp">{t.corporate_redirect}</p>
        </CardContent></Card>
      </div>

      {/* Chart */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <h4 className="text-sm font-semibold mb-3">Peticiones por mes</h4>
          {loading ? (
            <p className="text-muted-foreground text-sm">Cargando...</p>
          ) : (
            <div className="w-full h-[320px]" data-testid="report-chart">
              <ResponsiveContainer>
                <ComposedChart data={chartData} margin={{ top: 24, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                  <Tooltip content={<MonthlyChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Exitosas" stackId="a" fill="#10b981" />
                  <Bar dataKey="Validaciones" stackId="a" fill="#f59e0b" />
                  <Bar dataKey="Errores" stackId="a" fill="#ef4444" />
                  <Bar dataKey="Corporativas" stackId="a" fill="#c9a84c">
                    <LabelList dataKey="Total" position="top" fill="hsl(var(--foreground))" fontSize={11} fontWeight={600} formatter={(v) => v > 0 ? v : ''} />
                  </Bar>
                  <Line type="monotone" dataKey="Total" stroke="#c9a84c" strokeWidth={2} dot={{ r: 3, fill: "#c9a84c" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top errors */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold">Errores reales m&aacute;s frecuentes</h4>
            <p className="text-[10px] text-muted-foreground">API key, l&iacute;mites, plataforma</p>
          </div>
          {!(report?.top_errors?.length) ? (
            <p className="text-xs text-muted-foreground">Sin errores reales en el periodo. (Las validaciones de negocio se cuentan aparte.)</p>
          ) : (
            <div className="space-y-2" data-testid="top-errors-list">
              {report.top_errors.map((e, i) => (
                <div key={i} className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2">
                  <span className="text-sm truncate flex-1">{e.error_message}</span>
                  <Badge variant="secondary" className="ml-2 flex-shrink-0">{e.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ==================== CHATBOT EXPLORER (documentation-style) ====================
const METHOD_BADGE = {
  GET:    { className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
  POST:   { className: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
  PUT:    { className: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  DELETE: { className: "bg-red-500/15 text-red-400 border-red-500/40" },
};

// Helper to keep example payloads consistent with the backend contract.
const errExample = (code, message) => ({ error_code: code, message });

// Common error responses shared across endpoints (auth + rate limit + internal).
const SHARED_ERRORS = [
  { code: 401, description: "API key faltante o inv\u00e1lida",
    example: errExample("missing_api_key", "API key requerida (X-API-Key)") },
  { code: 429, description: "L\u00edmite de peticiones excedido (50/min por clave)",
    example: errExample("rate_limited", "L\u00edmite de peticiones excedido") },
  { code: 500, description: "Error interno del servidor",
    example: errExample("internal_error", "Error interno") },
];

const EXPLORER_ENDPOINTS = [
  {
    id: "venues_list", method: "GET", path: "/api/integrations/chatbot/venues",
    summary: "Listar sedes",
    description: "Devuelve todas las sedes activas. Permite filtrar por ciudad. \u00datil como primer paso del flujo del chatbot.",
    params: [
      { name: "city", in: "query", type: "string", required: false, placeholder: "Bogot\u00e1", description: "Filtra por ciudad exacta (sensible a may\u00fasculas/min\u00fasculas y tildes)." },
    ],
    body: null,
    responses: [
      { code: 200, description: "Lista de sedes activas", example: { venues: [{ id: "uuid", name: "Sede Centro", city: "Bogot\u00e1", address: "Carrera 7 #45-10", description: "...", image_url: "https://...", max_people: 12 }], count: 1 } },
      ...SHARED_ERRORS,
    ],
  },
  {
    id: "venue_detail", method: "GET", path: "/api/integrations/chatbot/venues/{venue_id}",
    summary: "Detalle de sede",
    description: "Devuelve la informaci\u00f3n completa de una sede: tipos de zona habilitados, motivos de reserva, condiciones efectivas y (opcional) horario efectivo para una fecha.",
    params: [
      { name: "venue_id", in: "path", type: "string", required: true, placeholder: "uuid de la sede", description: "Identificador de la sede." },
      { name: "date", in: "query", type: "string", required: false, placeholder: "2026-07-15", description: "Fecha opcional para resolver el horario efectivo (festivos, semanal, bloqueos)." },
    ],
    body: null,
    responses: [
      { code: 200, description: "Detalle de la sede", example: { id: "uuid", name: "Sede Centro", city: "Bogot\u00e1", time_interval: 30, zone_types_enabled: true, zone_types: [], reservation_purposes: [], conditions_enabled: true, conditions: [], schedule: { open: "11:00", close: "23:00", closed: false, source: "weekly:wed", time_interval: 30 } } },
      { code: 404, description: "Sede no encontrada",
        example: errExample("venue_not_found", "Sede no encontrada") },
      ...SHARED_ERRORS,
    ],
  },
  {
    id: "availability", method: "GET", path: "/api/integrations/chatbot/availability",
    summary: "Slots disponibles",
    description: "Devuelve los horarios disponibles para reservar en una sede y fecha. Replica la l\u00f3gica del wizard web: respeta festivos, horarios por d\u00eda, bloqueos por d\u00eda completo y por rango horario.",
    params: [
      { name: "sede_id", in: "query", type: "string", required: true, placeholder: "uuid de la sede", description: "Identificador de la sede." },
      { name: "date", in: "query", type: "string", required: true, placeholder: "2026-07-15", description: "Fecha objetivo (formato YYYY-MM-DD)." },
    ],
    body: null,
    responses: [
      { code: 200, description: "Horarios disponibles", example: { sede_id: "uuid", date: "2026-07-15", closed: false, schedule: { open: "11:00", close: "23:00" }, time_interval: 30, slots: [{ time: "11:00", time_label: "11:00 AM" }, { time: "11:30", time_label: "11:30 AM" }], count: 2 } },
      { code: 200, description: "D\u00eda cerrado (festivo / bloqueo full-day)", example: { sede_id: "uuid", date: "2026-12-25", closed: true, reason: "holiday", schedule: { closed: true, source: "holiday" }, slots: [] } },
      { code: 400, description: "Sede inactiva o configuraci\u00f3n inv\u00e1lida",
        example: errExample("venue_inactive", "Esta sede no est\u00e1 disponible") },
      { code: 404, description: "Sede no encontrada",
        example: errExample("venue_not_found", "Sede no encontrada") },
      ...SHARED_ERRORS,
    ],
  },
  {
    id: "reservation_create", method: "POST", path: "/api/integrations/chatbot/reservations",
    summary: "Crear reserva",
    description: "Crea una reserva confirmada inmediatamente (sin pago). Si num_people es mayor o igual al umbral corporativo configurado, crea la reserva con estado 'informational' (informativa, no admite acciones) y devuelve action='informational' junto con el link de WhatsApp como dato informativo.",
    params: [],
    body: {
      sede_id: "<uuid>",
      full_name: "Juan P\u00e9rez",
      email: "juan@example.com",
      phone: "+57 300 000 0000",
      num_people: 2,
      date: "2026-07-15",
      time: "19:00",
      reservation_purpose: "Cena",
      zone_type: null,
      comments: "",
    },
    body_schema: [
      { name: "sede_id", type: "string", required: true, description: "ID de la sede." },
      { name: "full_name", type: "string", required: true, description: "Nombre completo del cliente." },
      { name: "email", type: "string", required: true, description: "Email del cliente." },
      { name: "phone", type: "string", required: true, description: "Tel\u00e9fono de contacto." },
      { name: "num_people", type: "integer", required: true, description: "N\u00famero de personas. Si >= umbral corporativo, la reserva se crea como informativa (sin acciones)." },
      { name: "date", type: "string", required: true, description: "Fecha YYYY-MM-DD." },
      { name: "time", type: "string", required: true, description: "Hora HH:MM (24h). Use /availability para obtener slots v\u00e1lidos." },
      { name: "reservation_purpose", type: "string", required: true, description: "Motivo de la reserva." },
      { name: "zone_type", type: "string|null", required: false, description: "Tipo de zona (si la sede tiene zonas habilitadas)." },
      { name: "comments", type: "string", required: false, description: "Comentarios opcionales." },
    ],
    responses: [
      { code: 200, description: "Reserva creada", example: { action: "reservation_created", reservation: { id: "uuid", reservation_code: "RES-ABC123", status: "created", source: "chatbot" } } },
      { code: 200, description: "Reserva informativa (umbral corporativo)", example: { action: "informational", reservation: { id: "uuid", reservation_code: "RES-ABC123", status: "informational", is_corporate: true, source: "chatbot" }, whatsapp_link: "https://wa.me/57300", threshold: 10, message: "Para grupos de 10 o m\u00e1s personas..." } },
      { code: 400, description: "Validaci\u00f3n de negocio (horario, num_people, bloqueos, sede inactiva)",
        example: errExample("outside_schedule", "Horario fuera del rango de atenci\u00f3n (11:00-23:00)") },
      { code: 404, description: "Sede no encontrada",
        example: errExample("venue_not_found", "Sede no encontrada") },
      { code: 422, description: "Payload inv\u00e1lido (campo requerido faltante o tipo incorrecto)",
        example: errExample("invalid_request", "sede_id: campo requerido") },
      ...SHARED_ERRORS,
    ],
  },
  {
    id: "reservation_cancel", method: "POST", path: "/api/integrations/chatbot/reservations/{res_id}/cancel",
    summary: "Cancelar reserva",
    description: "Cancela una reserva existente. La integraci\u00f3n por chatbot no aplica la ventana de tiempo m\u00ednima de cancelaci\u00f3n (es confiable). Devuelve 409 si la reserva ya estaba cancelada.",
    params: [
      { name: "res_id", in: "path", type: "string", required: true, placeholder: "uuid de la reserva", description: "ID de la reserva." },
    ],
    body: { reason: "Cliente pidi\u00f3 cancelar" },
    body_schema: [
      { name: "reason", type: "string", required: false, description: "Motivo de la cancelaci\u00f3n (opcional)." },
    ],
    responses: [
      { code: 200, description: "Reserva cancelada", example: { action: "reservation_cancelled", reservation: { status: "cancelled", cancelled_by: "chatbot" } } },
      { code: 404, description: "Reserva no encontrada",
        example: errExample("reservation_not_found", "La reserva no existe.") },
      { code: 409, description: "Conflicto: la reserva ya estaba cancelada",
        example: errExample("already_cancelled", "La reserva ya ha sido cancelada.") },
      ...SHARED_ERRORS,
    ],
  },
];

function ChatbotExplorer() {
  const [configs, setConfigs] = useState([]);
  const [apiKey, setApiKey] = useState("");

  useEffect(() => {
    API.get("/admin/chatbot-configs").then(r => setConfigs(r.data || [])).catch(() => {});
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado")).catch(() => toast.error("Error al copiar"));
  };

  return (
    <div className="space-y-4" data-testid="chatbot-explorer">
      {/* Sticky API Key Header */}
      <Card className="bg-card border-border sticky top-0 z-10">
        <CardContent className="p-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                <Shield className="w-3 h-3" /> API key compartida (X-API-Key)
              </Label>
              <Input
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="cha_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="bg-background border-border font-mono text-xs"
                data-testid="explorer-api-key"
              />
            </div>
            {configs.length > 0 && (
              <p className="text-[10px] text-muted-foreground basis-full">
                Tienes {configs.length} clave(s) creada(s). Pega aquí la clave completa que se mostró al crearla.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Endpoint docs */}
      {EXPLORER_ENDPOINTS.map(ep => (
        <ExplorerEndpointCard key={ep.id} endpoint={ep} apiKey={apiKey} copy={copy} />
      ))}
    </div>
  );
}

function ExplorerEndpointCard({ endpoint, apiKey, copy }) {
  const [paramValues, setParamValues] = useState({});
  const [bodyText, setBodyText] = useState(endpoint.body ? JSON.stringify(endpoint.body, null, 2) : "");
  const [running, setRunning] = useState(false);
  const [response, setResponse] = useState(null);
  const [showTry, setShowTry] = useState(false);

  const badge = METHOD_BADGE[endpoint.method] || METHOD_BADGE.GET;

  const setParam = (name, value) => setParamValues(p => ({ ...p, [name]: value }));

  const buildUrl = () => {
    let path = endpoint.path;
    const queryParts = [];
    (endpoint.params || []).forEach(p => {
      const v = paramValues[p.name];
      if (p.in === "path") {
        path = path.replace(`{${p.name}}`, encodeURIComponent(v || `{${p.name}}`));
      } else if (p.in === "query" && v) {
        queryParts.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(v)}`);
      }
    });
    const backend = process.env.REACT_APP_BACKEND_URL;
    return backend + path + (queryParts.length ? `?${queryParts.join("&")}` : "");
  };

  const execute = async () => {
    if (!apiKey.trim()) { toast.error("Pega una API key en el campo superior"); return; }
    setRunning(true);
    setResponse(null);
    const t0 = performance.now();
    try {
      const url = buildUrl();
      const opts = {
        method: endpoint.method,
        headers: {
          "X-API-Key": apiKey.trim(),
          ...(endpoint.method !== "GET" ? { "Content-Type": "application/json" } : {}),
        },
      };
      if (endpoint.method !== "GET" && bodyText.trim()) {
        try { JSON.parse(bodyText); }
        catch { toast.error("JSON inv\u00e1lido en el body"); setRunning(false); return; }
        opts.body = bodyText;
      }
      const res = await fetch(url, opts);
      const elapsed = Math.round(performance.now() - t0);
      const ct = res.headers.get("content-type") || "";
      const parsed = ct.includes("json") ? await res.json() : await res.text();
      setResponse({ status: res.status, ok: res.ok, elapsed, body: parsed, url });
    } catch (e) {
      const elapsed = Math.round(performance.now() - t0);
      setResponse({ status: 0, ok: false, elapsed, body: { error: String(e) }, url: "" });
    } finally {
      setRunning(false);
    }
  };

  const statusBadgeClass = (code) => {
    if (code === 0) return "bg-red-500/15 text-red-400 border-red-500/40";
    if (code >= 200 && code < 300) return "bg-emerald-500/15 text-emerald-400 border-emerald-500/40";
    if (code >= 400 && code < 500) return "bg-amber-500/15 text-amber-400 border-amber-500/40";
    return "bg-red-500/15 text-red-400 border-red-500/40";
  };

  return (
    <Card className="bg-card border-border" data-testid={`explorer-endpoint-${endpoint.id}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <Badge variant="outline" className={`text-xs font-mono font-bold ${badge.className}`}>{endpoint.method}</Badge>
          <code className="text-sm font-mono flex-1 break-all">{endpoint.path}</code>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => copy(endpoint.path)} title="Copiar endpoint" data-testid={`copy-path-${endpoint.id}`}><Copy className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={() => setShowTry(s => !s)} data-testid={`toggle-try-${endpoint.id}`}>
            <Plug className="w-3.5 h-3.5 mr-1" /> {showTry ? "Cerrar" : "Try it"}
          </Button>
        </div>
        <h4 className="text-base font-semibold font-heading mb-1">{endpoint.summary}</h4>
        <p className="text-sm text-muted-foreground mb-4">{endpoint.description}</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT: documentation */}
          <div className="space-y-4">
            {(endpoint.params && endpoint.params.length > 0) && (
              <div>
                <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Par&aacute;metros</h5>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-background">
                      <tr className="text-muted-foreground">
                        <th className="text-left p-2 font-medium">Nombre</th>
                        <th className="text-left p-2 font-medium">Ubicaci&oacute;n</th>
                        <th className="text-left p-2 font-medium">Tipo</th>
                        <th className="text-left p-2 font-medium">Descripci&oacute;n</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoint.params.map(p => (
                        <tr key={p.name} className="border-t border-border">
                          <td className="p-2 font-mono">
                            {p.name}{p.required && <span className="text-red-500 ml-0.5">*</span>}
                          </td>
                          <td className="p-2"><Badge variant="outline" className="text-[10px]">{p.in}</Badge></td>
                          <td className="p-2 font-mono text-muted-foreground">{p.type}</td>
                          <td className="p-2 text-muted-foreground">{p.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {endpoint.body_schema && (
              <div>
                <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Body</h5>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-background">
                      <tr className="text-muted-foreground">
                        <th className="text-left p-2 font-medium">Campo</th>
                        <th className="text-left p-2 font-medium">Tipo</th>
                        <th className="text-left p-2 font-medium">Descripci&oacute;n</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoint.body_schema.map(f => (
                        <tr key={f.name} className="border-t border-border">
                          <td className="p-2 font-mono">{f.name}{f.required && <span className="text-red-500 ml-0.5">*</span>}</td>
                          <td className="p-2 font-mono text-muted-foreground">{f.type}</td>
                          <td className="p-2 text-muted-foreground">{f.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {endpoint.body && (
                  <div className="mt-2 relative">
                    <Label className="text-[10px] text-muted-foreground">Ejemplo</Label>
                    <pre className="bg-background border border-border rounded-lg p-3 text-xs font-mono mt-1 whitespace-pre-wrap break-all">{JSON.stringify(endpoint.body, null, 2)}</pre>
                    <Button size="sm" variant="ghost" className="absolute top-5 right-1 h-7 w-7 p-0" onClick={() => copy(JSON.stringify(endpoint.body, null, 2))} title="Copiar"><Copy className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
            )}

            <div>
              <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Respuestas</h5>
              <div className="space-y-2">
                {endpoint.responses.map((r, idx) => (
                  <div key={idx} className="border border-border rounded-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-background">
                      <Badge variant="outline" className={`text-[10px] font-mono ${statusBadgeClass(r.code)}`}>{r.code}</Badge>
                      <span className="text-xs text-muted-foreground">{r.description}</span>
                    </div>
                    <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-all border-t border-border">{JSON.stringify(r.example, null, 2)}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Try it */}
          {showTry && (
            <div className="border border-border rounded-lg p-4 bg-background/40 h-fit lg:sticky lg:top-20" data-testid={`try-it-${endpoint.id}`}>
              <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold flex items-center gap-1">
                <Plug className="w-3 h-3" /> Try it
              </h5>

              {(endpoint.params || []).length > 0 && (
                <div className="space-y-2 mb-3">
                  {endpoint.params.map(p => (
                    <div key={p.name}>
                      <Label className="text-[10px] text-muted-foreground font-mono">
                        {p.name}{p.required && <span className="text-red-500">*</span>}
                        <span className="ml-2 text-muted-foreground/60">({p.in})</span>
                      </Label>
                      <Input
                        value={paramValues[p.name] || ""}
                        onChange={e => setParam(p.name, e.target.value)}
                        placeholder={p.placeholder}
                        className="bg-background border-border text-xs font-mono mt-1"
                        data-testid={`tryit-param-${endpoint.id}-${p.name}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {endpoint.body && (
                <div className="mb-3">
                  <Label className="text-[10px] text-muted-foreground">Body (JSON)</Label>
                  <Textarea
                    value={bodyText}
                    onChange={e => setBodyText(e.target.value)}
                    rows={9}
                    className="bg-background border-border font-mono text-xs mt-1"
                    data-testid={`tryit-body-${endpoint.id}`}
                  />
                </div>
              )}

              <Button onClick={execute} disabled={running} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full" data-testid={`tryit-execute-${endpoint.id}`}>
                {running ? "Ejecutando..." : "Ejecutar"}
              </Button>

              {response && (
                <div className="mt-4 space-y-2" data-testid={`tryit-response-${endpoint.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-xs font-mono ${statusBadgeClass(response.status)}`}>{response.status || "Network"}</Badge>
                    <Badge variant="outline" className="text-[10px]">{response.elapsed} ms</Badge>
                  </div>
                  <pre className="bg-background border border-border rounded-lg p-3 text-xs font-mono whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">
                    {typeof response.body === "string" ? response.body : JSON.stringify(response.body, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== CHATBOT LOGS ====================
const STATUS_LABEL = {
  success: { label: "Exitoso", className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  validation_error: { label: "Validaci\u00f3n", className: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  error: { label: "Error", className: "bg-red-500/20 text-red-400 border-red-500/30" },
  corporate_redirect: { label: "Corporativo", className: "bg-primary/20 text-primary border-primary/30" },
};

function ChatbotLogs() {
  const [configs, setConfigs] = useState([]);
  const [filters, setFilters] = useState({ status: "all", configId: "all", endpoint: "", dateStart: "", dateEnd: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    API.get("/admin/chatbot-configs").then(r => setConfigs(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: 25 };
    if (filters.status !== "all") params.status = filters.status;
    if (filters.configId !== "all") params.chatbot_config_id = filters.configId;
    if (filters.endpoint.trim()) params.endpoint = filters.endpoint.trim();
    if (filters.dateStart) params.date_start = filters.dateStart;
    if (filters.dateEnd) params.date_end = filters.dateEnd;
    API.get("/admin/chatbot-logs", { params })
      .then(r => setData(r.data))
      .catch(() => toast.error("Error al cargar logs"))
      .finally(() => setLoading(false));
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const resetAndLoad = () => { setPage(1); };
  const clearDates = () => { setFilters(f => ({ ...f, dateStart: "", dateEnd: "" })); setPage(1); };

  return (
    <div className="space-y-4" data-testid="chatbot-logs">
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h4 className="text-sm font-semibold">Registro detallado</h4>
            <Badge variant="outline" className="text-[10px]" data-testid="logs-timezone-badge">
              <Clock className="w-3 h-3 mr-1" /> Zona horaria: {data.timezone || "America/Bogota"}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Estado</Label>
              <Select value={filters.status} onValueChange={v => { setFilters(f => ({ ...f, status: v })); setPage(1); }}>
                <SelectTrigger className="bg-background border-border" data-testid="logs-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="success">Exitoso</SelectItem>
                  <SelectItem value="validation_error">Validaci&oacute;n</SelectItem>
                  <SelectItem value="error">Error real</SelectItem>
                  <SelectItem value="corporate_redirect">Corporativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Integraci&oacute;n</Label>
              <Select value={filters.configId} onValueChange={v => { setFilters(f => ({ ...f, configId: v })); setPage(1); }}>
                <SelectTrigger className="bg-background border-border" data-testid="logs-filter-config"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {configs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground mb-1 block">Endpoint (contiene)</Label>
              <div className="flex gap-2">
                <Input value={filters.endpoint} onChange={e => setFilters(f => ({ ...f, endpoint: e.target.value }))} placeholder="reservations, venues..." className="bg-background border-border text-sm" data-testid="logs-filter-endpoint" onKeyDown={e => e.key === "Enter" && resetAndLoad()} />
                <Button size="sm" variant="outline" onClick={resetAndLoad}>Aplicar</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Desde</Label>
              <Input type="date" value={filters.dateStart} onChange={e => { setFilters(f => ({ ...f, dateStart: e.target.value })); setPage(1); }} className="bg-background border-border text-sm" data-testid="logs-filter-date-start" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Hasta</Label>
              <Input type="date" value={filters.dateEnd} onChange={e => { setFilters(f => ({ ...f, dateEnd: e.target.value })); setPage(1); }} className="bg-background border-border text-sm" data-testid="logs-filter-date-end" />
            </div>
            <div className="md:col-span-2 flex items-end">
              {(filters.dateStart || filters.dateEnd) && (
                <Button size="sm" variant="ghost" onClick={clearDates} className="text-xs" data-testid="logs-clear-dates">
                  <X className="w-3.5 h-3.5 mr-1" /> Limpiar fechas
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <p className="text-muted-foreground text-sm py-6 text-center">Cargando logs...</p>
          ) : data.items.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">Sin registros para los filtros aplicados.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="logs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>HTTP</TableHead>
                    <TableHead>M&eacute;todo</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Clave</TableHead>
                    <TableHead className="text-right">Tiempo</TableHead>
                    <TableHead className="text-right">Ver</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map(item => {
                    const meta = STATUS_LABEL[item.status] || { label: item.status, className: "" };
                    return (
                      <TableRow key={item.id} data-testid={`log-row-${item.id}`}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatInTz(item.created_at, data.timezone, true)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${meta.className}`}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{item.http_status || "-"}</TableCell>
                        <TableCell className="font-mono text-xs">{item.method}</TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[200px]" title={item.endpoint}>{item.endpoint}</TableCell>
                        <TableCell className="font-mono text-[10px] text-muted-foreground">{item.key_prefix || "-"}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">{item.response_time_ms} ms</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setSelected(item)} data-testid={`view-log-${item.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <span>P&aacute;gina {page} de {data.total_pages} ({data.total} entradas)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="logs-prev-page">Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} data-testid="logs-next-page">Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="log-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del registro</DialogTitle>
            <DialogDescription>{selected?.created_at ? formatInTz(selected.created_at, data.timezone, true) : ""}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><Label className="text-xs text-muted-foreground">Estado</Label><p>{(STATUS_LABEL[selected.status] || {}).label || selected.status}</p></div>
                <div><Label className="text-xs text-muted-foreground">HTTP</Label><p className="font-mono">{selected.http_status || "-"}</p></div>
                <div><Label className="text-xs text-muted-foreground">M&eacute;todo</Label><p className="font-mono">{selected.method}</p></div>
                <div><Label className="text-xs text-muted-foreground">Tiempo</Label><p>{selected.response_time_ms} ms</p></div>
                <div className="col-span-2"><Label className="text-xs text-muted-foreground">Endpoint</Label><p className="font-mono text-xs break-all">{selected.endpoint}</p></div>
                <div className="col-span-2"><Label className="text-xs text-muted-foreground">Clave (prefijo)</Label><p className="font-mono text-xs">{selected.key_prefix || "-"}</p></div>
              </div>
              {selected.error_message && (
                <div>
                  <Label className="text-xs text-muted-foreground">Mensaje de error</Label>
                  <pre className="bg-red-500/5 border border-red-500/30 rounded-lg p-3 text-xs whitespace-pre-wrap">{selected.error_message}</pre>
                </div>
              )}
              {selected.request_body && (
                <div>
                  <Label className="text-xs text-muted-foreground">Cuerpo de la petici&oacute;n</Label>
                  <pre className="bg-background border border-border rounded-lg p-3 text-xs font-mono whitespace-pre-wrap break-all">{JSON.stringify(selected.request_body, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== SETTINGS TAB ====================
function SettingsTab() {
  const [section, setSection] = useState("landing");
  const sections = [
    { id: "landing", l: "P\u00e1gina de inicio" },
    { id: "template", l: "Plantilla" },
    { id: "view", l: "Opciones de vista" },
    { id: "timezone", l: "Zona horaria" },
    { id: "cancellation", l: "Tiempos" },
    { id: "corporate", l: "Reservas corporativas" },
    { id: "cities", l: "Ciudades" },
    { id: "holidays", l: "Feriados" },
    { id: "smtp", l: "SMTP" },
  ];
  return (
    <div data-testid="settings-tab">
      <h2 className="text-2xl font-bold mb-6">Configuraci&oacute;n</h2>
      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map(s => (
          <Button key={s.id} data-testid={`settings-${s.id}`} size="sm" variant={section === s.id ? "default" : "outline"} className={section === s.id ? "bg-primary text-primary-foreground" : ""} onClick={() => setSection(s.id)}>{s.l}</Button>
        ))}
      </div>
      {section === "landing" && <LandingSettings />}
      {section === "template" && <TemplateSettings />}
      {section === "view" && <ViewSettings />}
      {section === "timezone" && <TimezoneSettings />}
      {section === "cancellation" && <CancellationSettings />}
      {section === "corporate" && <CorporateSettings />}
      {section === "cities" && <CitiesSettings />}
      {section === "holidays" && <HolidaysSettings />}
      {section === "smtp" && <SmtpSettings />}
    </div>
  );
}

function ViewSettings() {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: "light", icon: Sun, label: "Claro", desc: "Fondo claro con acentos dorados" },
    { value: "dark", icon: Moon, label: "Oscuro", desc: "Fondo oscuro con acentos dorados" },
    { value: "system", icon: Monitor, label: "Sistema", desc: "Sigue la preferencia de tu sistema operativo" },
  ];
  return (
    <div className="max-w-xl" data-testid="view-settings">
      <p className="text-sm text-muted-foreground mb-4">Selecciona el tema de la interfaz de administraci&oacute;n</p>
      <div className="space-y-2">
        {options.map(o => (
          <div key={o.value} data-testid={`theme-option-${o.value}`}
            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-primary/50 ${theme === o.value ? 'border-primary bg-primary/5' : 'border-border'}`}
            onClick={() => setTheme(o.value)}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === o.value ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              <o.icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold font-heading">{o.label}</h4>
              <p className="text-xs text-muted-foreground">{o.desc}</p>
            </div>
            {theme === o.value && <Check className="w-5 h-5 text-primary flex-shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function TimezoneSettings() {
  const [tz, setTz] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    API.get("/landing").then(r => setTz(r.data.timezone || "America/Bogota")).catch(() => setTz("America/Bogota"));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await API.put("/landing", { timezone: tz });
      toast.success("Zona horaria actualizada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };
  const timezones = [
    "America/Bogota", "America/Mexico_City", "America/New_York", "America/Chicago",
    "America/Denver", "America/Los_Angeles", "America/Lima", "America/Santiago",
    "America/Buenos_Aires", "America/Sao_Paulo", "America/Caracas", "America/Panama",
    "Europe/Madrid", "Europe/London", "Europe/Paris", "Europe/Berlin",
    "Asia/Tokyo", "Asia/Shanghai", "UTC",
  ];
  return (
    <div className="max-w-xl" data-testid="timezone-settings">
      <p className="text-sm text-muted-foreground mb-4">Configura la zona horaria para las reservas</p>
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Zona horaria</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger className="mt-1 bg-background border-border" data-testid="timezone-select">
              <SelectValue placeholder="Selecciona zona horaria" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {timezones.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="w-4 h-4 text-primary" />
          <span>Zona horaria actual: <strong className="text-foreground">{tz}</strong></span>
        </div>
        <Button data-testid="save-timezone-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={save} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

function LandingSettings() {
  const [config, setConfig] = useState({ title: "", subtitle: "", image_url: "", cta_text: "", template: "classic" });
  const [saving, setSaving] = useState(false);
  const [imageMode, setImageMode] = useState("url");
  const fileRef = useRef(null);
  useEffect(() => { API.get("/landing").then(r => setConfig(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await API.put("/landing", config); toast.success("Landing actualizado"); }
    catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setConfig(p => ({ ...p, image_url: data.url }));
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
  };
  return (
    <div className="max-w-xl space-y-4">
      <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Titulo</Label>
        <Input value={config.title} onChange={e => setConfig(p => ({ ...p, title: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Subtitulo</Label>
        <Textarea value={config.subtitle} onChange={e => setConfig(p => ({ ...p, subtitle: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Imagen principal</Label>
        <div className="flex gap-2 mt-1 mb-2">
          <Button type="button" size="sm" variant={imageMode === "url" ? "default" : "outline"} className={imageMode === "url" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("url")} data-testid="landing-image-url-mode"><Link2 className="w-3 h-3 mr-1" /> URL</Button>
          <Button type="button" size="sm" variant={imageMode === "upload" ? "default" : "outline"} className={imageMode === "upload" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("upload")} data-testid="landing-image-upload-mode"><Upload className="w-3 h-3 mr-1" /> Subir</Button>
        </div>
        {imageMode === "url" ? (
          <Input data-testid="landing-image-url" value={config.image_url} onChange={e => setConfig(p => ({ ...p, image_url: e.target.value }))} className="bg-background border-border" placeholder="https://..." />
        ) : (
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
            <Button type="button" variant="outline" className="w-full border-dashed border-border" onClick={() => fileRef.current?.click()} data-testid="landing-image-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Seleccionar imagen
            </Button>
          </div>
        )}
        {config.image_url && (
          <div className="mt-2 rounded-lg overflow-hidden border border-border">
            <img src={config.image_url} alt="Preview" className="w-full h-32 object-cover" onError={e => { e.target.style.display = 'none'; }} />
          </div>
        )}
      </div>
      <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Texto del boton</Label>
        <Input value={config.cta_text} onChange={e => setConfig(p => ({ ...p, cta_text: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      <Button data-testid="save-landing-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={save} disabled={saving}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
}

function TemplateSettings() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { API.get("/landing").then(r => setConfig(r.data)).catch(() => {}); }, []);
  const save = async (template) => {
    setSaving(true);
    try {
      await API.put("/landing", { template });
      setConfig(p => ({ ...p, template }));
      toast.success("Plantilla actualizada");
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  if (!config) return null;
  return (
    <div className="max-w-2xl" data-testid="template-settings">
      <p className="text-sm text-muted-foreground mb-4">Selecciona c&oacute;mo se ver&aacute; tu p&aacute;gina p&uacute;blica</p>
      <div className="grid grid-cols-2 gap-4">
        {[
          { id: "classic", name: "Classic", desc: "Imagen de fondo completa con contenido centrado y efecto de cristal" },
          { id: "modern", name: "Modern", desc: "Dise\u00f1o dividido con imagen a la izquierda y contenido a la derecha" },
        ].map(t => (
          <div key={t.id} data-testid={`template-${t.id}`}
            className={`cursor-pointer rounded-xl border-2 p-4 transition-all hover:border-primary/50 ${config.template === t.id ? 'border-primary bg-primary/5' : 'border-border'}`}
            onClick={() => save(t.id)}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm font-heading">{t.name}</h3>
              {config.template === t.id && <Check className="w-4 h-4 text-primary" />}
            </div>
            <p className="text-xs text-muted-foreground">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CancellationSettings() {
  const [hours, setHours] = useState(2);
  const [leadHours, setLeadHours] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    API.get("/landing").then(r => {
      setHours(r.data.cancellation_hours || 2);
      setLeadHours(r.data.booking_lead_hours || 0);
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await API.put("/landing", { cancellation_hours: parseInt(hours) || 2, booking_lead_hours: parseInt(leadHours) || 0 });
      toast.success("Pol\u00edtica de cancelaci\u00f3n actualizada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl" data-testid="cancellation-settings">
      <p className="text-sm text-muted-foreground mb-4">Define hasta cu&aacute;ntas horas antes de la reserva un cliente puede cancelar</p>
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Horas de anticipaci&oacute;n para cancelar</Label>
          <div className="flex items-center gap-3 mt-2">
            <Input type="number" min={0} max={72} value={hours} onChange={e => setHours(e.target.value)} className="w-24 bg-background border-border" data-testid="cancellation-hours-input" />
            <span className="text-sm text-muted-foreground">horas antes de la reserva</span>
          </div>
        </div>
        <div className="p-3 bg-secondary/50 rounded-lg text-sm text-muted-foreground">
          Ejemplo: Si el valor es <strong className="text-foreground">{hours}</strong>, un cliente podra cancelar hasta <strong className="text-foreground">{hours} horas</strong> antes de la hora de su reserva. Despues de ese limite, solo Admin o Sede podran cancelar.
        </div>

        <div className="pt-4 border-t border-border">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Horas previas de bloqueo <span className="normal-case text-primary">(nuevo)</span></Label>
          <p className="text-sm text-muted-foreground mt-1 mb-2">Define con cu&aacute;ntas horas de anticipaci&oacute;n se bloquea autom&aacute;ticamente un horario para que nadie pueda reservar tan inmediato.</p>
          <div className="flex items-center gap-3 mt-2">
            <Input type="number" min={0} max={72} value={leadHours} onChange={e => setLeadHours(e.target.value)} className="w-24 bg-background border-border" data-testid="booking-lead-hours-input" />
            <span className="text-sm text-muted-foreground">horas de anticipaci&oacute;n para reservar</span>
          </div>
          <div className="p-3 bg-secondary/50 rounded-lg text-sm text-muted-foreground mt-2">
            Ejemplo: Si el valor es <strong className="text-foreground">{leadHours}</strong>, las reservas se bloquean con <strong className="text-foreground">{leadHours} horas</strong> de anticipaci&oacute;n. Si son las 2:00 p.m. y el valor es 3, el cliente solo podr&aacute; reservar a partir de las 5:00 p.m. {parseInt(leadHours) === 0 ? "(Con 0 no se aplica ning\u00fan bloqueo por anticipaci\u00f3n.)" : ""}
          </div>
        </div>

        <Button data-testid="save-cancellation-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={save} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

function CorporateSettings() {
  const [link, setLink] = useState("");
  const [threshold, setThreshold] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    API.get("/landing").then(r => {
      setLink(r.data.whatsapp_link || "");
      setThreshold(r.data.corporate_threshold || 0);
    }).catch(() => {});
  }, []);
  const save = async () => {
    const trimmed = (link || "").trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed) && !/^wa\.me\//i.test(trimmed)) {
      toast.error("Enlace invalido. Usa https://wa.me/573001234567");
      return;
    }
    setSaving(true);
    try {
      await API.put("/landing", { whatsapp_link: trimmed, corporate_threshold: parseInt(threshold) || 0 });
      toast.success("Configuraci\u00f3n corporativa guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl" data-testid="corporate-settings">
      <p className="text-sm text-muted-foreground mb-4">
        Redirige reservas de grupos grandes a WhatsApp con un mensaje pre-llenado. Dejar el umbral en <strong>0</strong> para desactivar.
      </p>
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Enlace de WhatsApp</Label>
          <Input
            type="url"
            placeholder="https://wa.me/573001234567"
            value={link}
            onChange={e => setLink(e.target.value)}
            className="mt-2 bg-background border-border"
            data-testid="whatsapp-link-input"
          />
          <p className="text-xs text-muted-foreground mt-1">Formato: https://wa.me/&lt;codigo pais&gt;&lt;numero sin signos&gt;</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Umbral de personas (grupos corporativos)</Label>
          <div className="flex items-center gap-3 mt-2">
            <Input
              type="number"
              min={0}
              max={500}
              value={threshold}
              onChange={e => setThreshold(e.target.value)}
              className="w-28 bg-background border-border"
              data-testid="corporate-threshold-input"
            />
            <span className="text-sm text-muted-foreground">personas o m&aacute;s</span>
          </div>
        </div>
        <div className="p-3 bg-secondary/50 rounded-lg text-sm text-muted-foreground">
          {threshold > 0
            ? <>Los clientes que seleccionen <strong className="text-foreground">&quot;M&aacute;s de {threshold} personas&quot;</strong> ser&aacute;n redirigidos a WhatsApp con los datos pre-llenados.</>
            : <>La funci&oacute;n corporativa est&aacute; <strong className="text-foreground">desactivada</strong>. Configura un umbral mayor a 0 para activarla.</>
          }
        </div>
        <Button data-testid="save-corporate-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={save} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

function CitiesSettings() {
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/landing").then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      await API.put("/landing", { cities: next });
      setCities(next);
      toast.success("Ciudades actualizadas");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const addCity = () => {
    const v = (newCity || "").trim();
    if (!v) return;
    if (cities.find(c => c.toLowerCase() === v.toLowerCase())) {
      toast.error("Esa ciudad ya existe");
      return;
    }
    const next = [...cities, v].sort((a, b) => a.localeCompare(b, "es"));
    persist(next);
    setNewCity("");
  };

  const removeCity = (name) => {
    const next = cities.filter(c => c !== name);
    persist(next);
  };

  return (
    <div className="max-w-xl" data-testid="cities-settings">
      <p className="text-sm text-muted-foreground mb-4">
        Listado de ciudades disponibles al crear o editar una sede. Se ordenan alfab&eacute;ticamente.
      </p>
      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Nueva ciudad"
          value={newCity}
          onChange={e => setNewCity(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
          className="bg-background border-border"
          data-testid="city-input"
        />
        <Button data-testid="add-city-btn" onClick={addCity} disabled={saving || !newCity.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {cities.length === 0 && <p className="text-xs text-muted-foreground">No hay ciudades configuradas</p>}
        {cities.map(c => (
          <Badge key={c} variant="outline" className="border-border pl-3 pr-1 py-1 text-sm font-normal" data-testid={`city-badge-${c}`}>
            {c}
            <button type="button" onClick={() => removeCity(c)} className="ml-2 p-1 rounded-full hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-city-${c}`} aria-label={`Eliminar ${c}`}>
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function HolidaysSettings() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", description: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    API.get("/holidays").then(r => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.date) { toast.error("La fecha es requerida"); return; }
    setBusy(true);
    try {
      await API.post("/holidays", form);
      setForm({ date: "", description: "" });
      load();
      toast.success("Feriado agregado");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await API.delete(`/holidays/${id}`); load(); toast.success("Feriado eliminado"); }
    catch { toast.error("Error"); }
  };

  const downloadTemplate = () => {
    const csv = "date,description\n2026-01-01,A\u00f1o Nuevo\n2026-05-01,D\u00eda del Trabajo\n2026-12-25,Navidad\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "feriados_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile = () => fileRef.current && fileRef.current.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      // Skip header if present
      const header = lines[0].toLowerCase();
      const rows = (header.includes("date") ? lines.slice(1) : lines).map(line => {
        const [date, ...rest] = line.split(",");
        return { date: (date || "").trim(), description: rest.join(",").trim() };
      }).filter(r => r.date);
      const { data } = await API.post("/holidays/bulk", { items: rows });
      toast.success(`Importados: ${data.inserted} | Duplicados: ${data.skipped}` + (data.errors?.length ? ` | Errores: ${data.errors.length}` : ""));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-2xl" data-testid="holidays-settings">
      <p className="text-sm text-muted-foreground mb-4">
        Listado de feriados. Se aplican a todas las sedes y permiten un horario distinto v&iacute;a &quot;Feriados&quot; en los horarios de cada sede.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-48 bg-background border-border" data-testid="holiday-date-input" />
        <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Descripción (ej: Día de la Madre)" className="flex-1 min-w-[200px] bg-background border-border" data-testid="holiday-description-input" />
        <Button data-testid="add-holiday-btn" onClick={add} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="holiday-download-template-btn">
          <Upload className="w-3 h-3 mr-1 rotate-180" /> Descargar plantilla CSV
        </Button>
        <Button variant="outline" size="sm" onClick={onPickFile} disabled={busy} data-testid="holiday-import-csv-btn">
          <Upload className="w-3 h-3 mr-1" /> Importar CSV
        </Button>
        <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" />
      </div>
      <div className="space-y-1 border border-border rounded-lg p-2 max-h-96 overflow-y-auto">
        {items.length === 0 && <p className="text-xs text-muted-foreground p-2">No hay feriados configurados.</p>}
        {items.map(h => (
          <div key={h.id} className="flex items-center justify-between bg-card border border-border/40 rounded-md p-2" data-testid={`holiday-row-${h.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{formatDateStr(h.date)}</p>
                {h.description && <p className="text-xs text-muted-foreground truncate">{h.description}</p>}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-900/20" onClick={() => remove(h.id)} data-testid={`delete-holiday-${h.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}


function CityAutocomplete({ value, onChange, options, placeholder = "Selecciona una ciudad", testId = "city-autocomplete" }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const handleOutside = useCallback((e) => {
    if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [handleOutside]);

  const filtered = (options || []).filter(c => c.toLowerCase().includes((query || "").toLowerCase())).slice(0, 50);

  const pick = (c) => {
    setQuery(c);
    onChange(c);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <Input
        type="text"
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={e => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(filtered.length - 1, h + 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)); }
          else if (e.key === "Enter" && open && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight]); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
        className="bg-background border-border"
        data-testid={`${testId}-input`}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg" data-testid={`${testId}-list`}>
          {filtered.map((c, i) => (
            <button
              key={c}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(c)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${i === highlight ? 'bg-primary/10 text-foreground' : 'hover:bg-secondary'} ${value === c ? 'font-semibold text-primary' : ''}`}
              data-testid={`${testId}-option-${c}`}
            >{c}</button>
          ))}
        </div>
      )}
      {open && query && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-card shadow-lg px-3 py-2 text-xs text-muted-foreground">
          No se encontraron ciudades
        </div>
      )}
    </div>
  );
}

function SmtpSettings() {
  const [config, setConfig] = useState({ host: "", port: 587, username: "", password: "", from_email: "", enabled: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { API.get("/admin/smtp").then(r => setConfig(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await API.put("/admin/smtp", config); toast.success("SMTP actualizado"); }
    catch { toast.error("Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl space-y-4">
      <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm font-medium">Habilitar envio de correos</span>
          <span className="text-xs text-muted-foreground">Activar el envio real de emails via SMTP</span>
        </div>
        <button data-testid="smtp-toggle" className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${config.enabled ? 'bg-primary' : 'bg-secondary'}`}
          onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-foreground transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      <div><Label className="text-xs text-muted-foreground">Host SMTP</Label><Input value={config.host} onChange={e => setConfig(p => ({ ...p, host: e.target.value }))} className="mt-1 bg-background border-border" placeholder="smtp.gmail.com" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs text-muted-foreground">Puerto</Label><Input type="number" value={config.port} onChange={e => setConfig(p => ({ ...p, port: parseInt(e.target.value) || 587 }))} className="mt-1 bg-background border-border" /></div>
        <div><Label className="text-xs text-muted-foreground">Email remitente</Label><Input value={config.from_email} onChange={e => setConfig(p => ({ ...p, from_email: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      </div>
      <div><Label className="text-xs text-muted-foreground">Usuario</Label><Input value={config.username} onChange={e => setConfig(p => ({ ...p, username: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      <div><Label className="text-xs text-muted-foreground">Contrasena</Label><Input type="password" value={config.password} onChange={e => setConfig(p => ({ ...p, password: e.target.value }))} className="mt-1 bg-background border-border" /></div>
      <Button data-testid="save-smtp-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" onClick={save} disabled={saving}>
        {saving ? "Guardando..." : "Guardar"}
      </Button>
    </div>
  );
}

// ==================== SEDES TAB ====================
const SCHEDULE_DAYS = [
  { key: "mon", label: "Lunes" },
  { key: "tue", label: "Martes" },
  { key: "wed", label: "Mi\u00e9rcoles" },
  { key: "thu", label: "Jueves" },
  { key: "fri", label: "Viernes" },
  { key: "sat", label: "S\u00e1bado" },
  { key: "sun", label: "Domingo" },
  { key: "holiday", label: "Feriados" },
];

function defaultSchedules() {
  return SCHEDULE_DAYS.map(d => ({ day: d.key, open_time: "12:00", close_time: "21:00", closed: false }));
}

function mergeSchedules(existing, legacyOpen, legacyClose) {
  // Build a complete list ensuring all 8 day-keys exist using existing values when present.
  const map = new Map((existing || []).map(s => [s.day, s]));
  return SCHEDULE_DAYS.map(d => {
    const found = map.get(d.key);
    if (found) return {
      day: d.key,
      open_time: found.open_time || legacyOpen || "12:00",
      close_time: found.close_time || legacyClose || "21:00",
      closed: !!found.closed,
    };
    return { day: d.key, open_time: legacyOpen || "12:00", close_time: legacyClose || "21:00", closed: false };
  });
}

function SedesTab() {
  const [sedes, setSedes] = useState([]);
  const [zoneTypes, setZoneTypes] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", address: "", image_url: "", max_people: 10, opening_time: "12:00", closing_time: "21:00", time_interval: 30, zone_types_enabled: false, assigned_zone_type_ids: [], reservation_purpose_ids: [], show_conditions: true, is_active: true, city: "", schedules: defaultSchedules() });
  const [imageMode, setImageMode] = useState("url");
  const [sedeConditions, setSedeConditions] = useState([]);
  const [cities, setCities] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(() => { API.get("/sedes?include_inactive=true").then(r => setSedes(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    API.get("/zone-types").then(r => setZoneTypes(r.data)).catch(() => {});
    API.get("/landing").then(r => setCities(r.data.cities || [])).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", address: "", image_url: "", max_people: 10, opening_time: "12:00", closing_time: "21:00", time_interval: 30, zone_types_enabled: false, assigned_zone_type_ids: [], reservation_purpose_ids: [], show_conditions: true, is_active: true, city: "", schedules: defaultSchedules() }); setSedeConditions([]); setImageMode("url"); setDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", address: s.address || "", image_url: s.image_url || "", max_people: s.max_people || 10, opening_time: s.opening_time || "12:00", closing_time: s.closing_time || "21:00", time_interval: s.time_interval || 30, zone_types_enabled: s.zone_types_enabled || false, assigned_zone_type_ids: s.assigned_zone_type_ids || [], reservation_purpose_ids: s.reservation_purpose_ids || [], show_conditions: s.show_conditions !== false, is_active: s.is_active !== false, city: s.city || "", schedules: mergeSchedules(s.schedules, s.opening_time, s.closing_time), excluded_condition_ids: s.excluded_condition_ids || [] });
    setImageMode("url"); setDialog(true);
    // Load globals + sede-specific conditions for the conditions section
    Promise.all([
      API.get(`/conditions?scope=global`).catch(() => ({ data: [] })),
      API.get(`/conditions?sede_id=${s.id}`).catch(() => ({ data: [] })),
    ]).then(([gRes, eRes]) => {
      const globals = gRes.data.map(g => ({ ...g, is_global: true }));
      const specifics = (eRes.data || []).filter(c => !c.is_global);
      const merged = [...globals, ...specifics].sort((a, b) => (a.order || 0) - (b.order || 0));
      setSedeConditions(merged);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, image_url: data.url }));
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
  };

  const toggleTableType = (ttId) => {
    setForm(p => {
      const ids = [...(p.assigned_zone_type_ids || [])];
      if (ids.includes(ttId)) return { ...p, assigned_zone_type_ids: ids.filter(id => id !== ttId) };
      return { ...p, assigned_zone_type_ids: [...ids, ttId] };
    });
  };

  const save = async () => {
    try {
      if (editing) await API.put(`/sedes/${editing.id}`, form);
      else await API.post("/sedes", form);
      toast.success(editing ? "Sede actualizada" : "Sede creada");
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await API.delete(`/sedes/${id}`); toast.success("Sede eliminada"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  return (
    <div data-testid="sedes-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Sedes</h2>
        <Button data-testid="add-sede-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={openNew}><Plus className="w-4 h-4 mr-1" /> Nueva Sede</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sedes.map(s => (
          <Card key={s.id} className={`bg-card border-border overflow-hidden ${s.is_active === false ? 'opacity-75' : ''}`} data-testid={`admin-sede-card-${s.id}`}>
            {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-32 object-cover" />}
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold font-heading">{s.name}</h3>
                <Badge className={s.is_active === false ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary border border-primary/20"} variant="outline" data-testid={`sede-status-${s.id}`}>
                  {s.is_active === false ? "Inactiva" : "Activa"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {s.city && <span className="font-medium text-foreground/80">{s.city}</span>}
                {s.city && s.address && <span> · </span>}
                {s.address}
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(s)} data-testid={`edit-sede-${s.id}`}><Pencil className="w-3 h-3 mr-1" /> Editar</Button>
                <Button size="sm" variant="outline" className="border-red-800 text-red-400 hover:bg-red-900/20" onClick={() => remove(s.id)} data-testid={`delete-sede-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>{editing ? "Editar Sede" : "Nueva Sede"}</DialogTitle></DialogHeader>
          <div className="space-y-3 min-w-0">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-secondary/30">
              <div className="min-w-0">
                <Label className="text-sm font-semibold">Sede activa</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Si est&aacute; inactiva, no aparece en la p&aacute;gina p&uacute;blica ni acepta reservas nuevas.</p>
              </div>
              <button type="button" data-testid="sede-active-toggle"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${form.is_active ? 'bg-primary' : 'bg-secondary border border-border'}`}
                onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${form.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div><Label className="text-xs text-muted-foreground">Nombre</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Descripci&oacute;n</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Direcci&oacute;n</Label><Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Ciudad</Label>
              <div className="mt-1">
                <CityAutocomplete
                  value={form.city}
                  onChange={(v) => setForm(p => ({ ...p, city: v }))}
                  options={cities}
                  testId="sede-city"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Imagen</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <Button type="button" size="sm" variant={imageMode === "url" ? "default" : "outline"} className={imageMode === "url" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("url")}><Link2 className="w-3 h-3 mr-1" /> URL</Button>
                <Button type="button" size="sm" variant={imageMode === "upload" ? "default" : "outline"} className={imageMode === "upload" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("upload")}><Upload className="w-3 h-3 mr-1" /> Subir</Button>
              </div>
              {imageMode === "url" ? (
                <Input data-testid="sede-image-url" value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} className="bg-background border-border" placeholder="https://..." />
              ) : (
                <div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  <Button type="button" variant="outline" className="w-full border-dashed border-border" onClick={() => fileRef.current?.click()} data-testid="sede-image-upload">
                    <Upload className="w-4 h-4 mr-2" /> Seleccionar imagen
                  </Button>
                </div>
              )}
              {form.image_url && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border">
                  <img src={form.image_url} alt="Preview" className="w-full h-24 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Max Personas (Reserva)</Label><Input type="number" value={form.max_people} onChange={e => setForm(p => ({ ...p, max_people: parseInt(e.target.value) || 10 }))} className="mt-1 bg-background border-border" /></div>
              <div><Label className="text-xs text-muted-foreground">Intervalo (min)</Label><Input type="number" value={form.time_interval} onChange={e => setForm(p => ({ ...p, time_interval: parseInt(e.target.value) || 30 }))} className="mt-1 bg-background border-border" /></div>
            </div>
            <div className="border border-border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Horarios de atenci&oacute;n</Label>
                <span className="text-[10px] text-muted-foreground">Si cierre &le; apertura, termina al d&iacute;a siguiente</span>
              </div>
              <div className="space-y-1">
                {form.schedules.map((sch, idx) => {
                  const dayLabel = SCHEDULE_DAYS.find(d => d.key === sch.day)?.label || sch.day;
                  const update = (patch) => setForm(p => ({ ...p, schedules: p.schedules.map((x, i) => i === idx ? { ...x, ...patch } : x) }));
                  return (
                    <div key={sch.day} className={`grid grid-cols-[80px_1fr_1fr_auto] items-center gap-2 ${sch.closed ? 'opacity-50' : ''}`} data-testid={`schedule-row-${sch.day}`}>
                      <span className={`text-xs ${sch.day === 'holiday' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{dayLabel}</span>
                      <Input type="time" value={sch.open_time} disabled={sch.closed} onChange={e => update({ open_time: e.target.value })} className="h-8 bg-background border-border text-xs" data-testid={`schedule-${sch.day}-open`} />
                      <Input type="time" value={sch.close_time} disabled={sch.closed} onChange={e => update({ close_time: e.target.value })} className="h-8 bg-background border-border text-xs" data-testid={`schedule-${sch.day}-close`} />
                      <button type="button" onClick={() => update({ closed: !sch.closed })}
                        className={`h-7 px-2 rounded-md text-[10px] border ${sch.closed ? 'bg-secondary text-muted-foreground border-border' : 'bg-primary/10 text-primary border-primary/30'}`}
                        data-testid={`schedule-${sch.day}-toggle`}>
                        {sch.closed ? 'Cerrado' : 'Abierto'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            {zoneTypes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-muted-foreground">Tipos de zona</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-muted-foreground">Habilitar</span>
                    <button type="button" className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${form.zone_types_enabled ? 'bg-primary' : 'bg-secondary'}`}
                      onClick={() => setForm(p => ({ ...p, zone_types_enabled: !p.zone_types_enabled }))}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${form.zone_types_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </label>
                </div>
                {form.zone_types_enabled && (
                  <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                    {zoneTypes.map(tt => (
                      <label key={tt.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary cursor-pointer">
                        <input type="checkbox" checked={form.assigned_zone_type_ids?.includes(tt.id)} onChange={() => toggleTableType(tt.id)} className="rounded border-border" />
                        <span className="text-sm">{tt.name}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{tt.max_seats} personas</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Conditions toggle */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Condiciones de reserva</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-muted-foreground">Mostrar al cliente</span>
                  <button type="button" data-testid="sede-form-conditions-toggle" className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${form.show_conditions ? 'bg-primary' : 'bg-secondary'}`}
                    onClick={() => setForm(p => ({ ...p, show_conditions: !p.show_conditions }))}>
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${form.show_conditions ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              </div>
              {form.show_conditions && sedeConditions.length > 0 && (
                <div className="space-y-1 max-h-64 overflow-y-auto overflow-x-hidden border border-border rounded-lg p-2">
                  {sedeConditions.map(c => {
                    const isGlobal = !!c.is_global;
                    // For globals the checkbox reflects "included in this sede" (NOT excluded).
                    // For specifics it reflects the global `active` flag.
                    const isExcluded = (form.excluded_condition_ids || []).includes(c.id);
                    const checked = isGlobal ? !isExcluded : c.active !== false;
                    const dim = isGlobal ? isExcluded : c.active === false;
                    return (
                      <label key={c.id} className={`flex items-start gap-2 p-2 rounded-lg hover:bg-secondary cursor-pointer min-w-0 ${dim ? 'opacity-50' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={async () => {
                          if (isGlobal) {
                            // Toggle exclusion only for THIS sede (not affecting the global)
                            if (!editing) {
                              // Local form change for "new" sedes that haven't been persisted yet.
                              setForm(p => {
                                const cur = p.excluded_condition_ids || [];
                                const next = cur.includes(c.id) ? cur.filter(x => x !== c.id) : [...cur, c.id];
                                return { ...p, excluded_condition_ids: next };
                              });
                              return;
                            }
                            try {
                              const cur = form.excluded_condition_ids || [];
                              const next = cur.includes(c.id) ? cur.filter(x => x !== c.id) : [...cur, c.id];
                              await API.put(`/sedes/${editing.id}/condition-exclusions`, { excluded_condition_ids: next });
                              setForm(p => ({ ...p, excluded_condition_ids: next }));
                              toast.success(cur.includes(c.id) ? "Incluida en esta sede" : "Excluida solo en esta sede");
                            } catch { toast.error("Error al actualizar"); }
                          } else {
                            try {
                              const { data } = await API.patch(`/conditions/${c.id}/toggle`);
                              setSedeConditions(prev => prev.map(x => x.id === c.id ? { ...x, active: data.active } : x));
                            } catch { toast.error("Error al cambiar estado"); }
                          }
                        }} className="rounded border-border mt-1 flex-shrink-0" />
                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <IconDisplay name={c.icon} className="w-3 h-3 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm break-words">{c.title}</span>
                            {isGlobal && <Badge variant="outline" className="border-primary/30 text-primary text-[10px] py-0">Global</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground break-words line-clamp-2">{c.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {form.show_conditions && sedeConditions.length === 0 && editing && (
                <p className="text-xs text-muted-foreground border border-border rounded-lg p-2">Sin condiciones. Agrega desde la pesta&ntilde;a &quot;Condiciones&quot;.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="save-sede-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== TABLE TYPES TAB ====================
function ZoneTypesTab() {
  const [zoneTypes, setZoneTypes] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", max_seats: 10, image_url: "" });
  const [imageMode, setImageMode] = useState("url");
  const fileRef = useRef(null);

  const load = useCallback(() => { API.get("/zone-types").then(r => setZoneTypes(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", max_seats: 10, image_url: "" }); setImageMode("url"); setDialog(true); };
  const openEdit = (tt) => { setEditing(tt); setForm({ name: tt.name, description: tt.description || "", max_seats: tt.max_seats || 10, image_url: tt.image_url || "" }); setImageMode("url"); setDialog(true); };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, image_url: data.url }));
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    try {
      if (editing) await API.put(`/zone-types/${editing.id}`, form);
      else await API.post("/zone-types", form);
      toast.success(editing ? "Tipo de zona actualizado" : "Tipo de zona creado");
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await API.delete(`/zone-types/${id}`); toast.success("Tipo de zona eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  return (
    <div data-testid="zone-types-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Tipos de Zona</h2>
        <Button data-testid="add-table-type-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={openNew}><Plus className="w-4 h-4 mr-1" /> Nuevo Tipo</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {zoneTypes.map(tt => (
          <Card key={tt.id} className="bg-card border-border overflow-hidden" data-testid={`table-type-card-${tt.id}`}>
            {tt.image_url && <img src={tt.image_url} alt={tt.name} className="w-full h-32 object-cover" />}
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-sm font-heading">{tt.name}</h3>
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">{tt.max_seats} personas</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-3">{tt.description}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(tt)}><Pencil className="w-3 h-3 mr-1" /> Editar</Button>
                <Button size="sm" variant="outline" className="border-red-800 text-red-400 hover:bg-red-900/20" onClick={() => remove(tt.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {zoneTypes.length === 0 && <p className="text-muted-foreground col-span-3">No hay tipos de zona creados</p>}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Tipo de Zona" : "Nuevo Tipo de Zona"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Nombre *</Label><Input data-testid="tt-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-background border-border" placeholder="Ej: Terraza, VIP, Sal&oacute;n..." /></div>
            <div><Label className="text-xs text-muted-foreground">Descripci&oacute;n</Label><Textarea data-testid="tt-description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 bg-background border-border" placeholder="Describe este tipo de zona..." /></div>
            <div><Label className="text-xs text-muted-foreground">M&aacute;ximo de personas</Label><Input data-testid="tt-max-seats" type="number" min={1} value={form.max_seats} onChange={e => setForm(p => ({ ...p, max_seats: parseInt(e.target.value) || 1 }))} className="mt-1 bg-background border-border" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Imagen</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <Button type="button" size="sm" variant={imageMode === "url" ? "default" : "outline"} className={imageMode === "url" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("url")}><Link2 className="w-3 h-3 mr-1" /> URL</Button>
                <Button type="button" size="sm" variant={imageMode === "upload" ? "default" : "outline"} className={imageMode === "upload" ? "bg-primary text-primary-foreground" : ""} onClick={() => setImageMode("upload")}><Upload className="w-3 h-3 mr-1" /> Subir</Button>
              </div>
              {imageMode === "url" ? (
                <Input data-testid="tt-image-url" value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} className="bg-background border-border" placeholder="https://..." />
              ) : (
                <div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  <Button type="button" variant="outline" className="w-full border-dashed border-border" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Seleccionar imagen</Button>
                </div>
              )}
              {form.image_url && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border">
                  <img src={form.image_url} alt="Preview" className="w-full h-28 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="save-table-type-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== EVENTS TAB ====================
function ReservationPurposesTab() {
  const [purposes, setPurposes] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", icon: "calendar", active: true });
  const [iconPicker, setIconPicker] = useState(false);

  const load = useCallback(() => { API.get("/reservation-purposes").then(r => setPurposes(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", icon: "calendar", active: true }); setDialog(true); };
  const openEdit = (e) => { setEditing(e); setForm({ name: e.name, icon: e.icon, active: e.active }); setDialog(true); };
  const save = async () => {
    try {
      if (editing) await API.put(`/reservation-purposes/${editing.id}`, form);
      else await API.post("/reservation-purposes", form);
      toast.success(editing ? "Motivo actualizado" : "Motivo creado");
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await API.delete(`/reservation-purposes/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="reservation-purposes-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Motivos de Reserva</h2>
        <Button data-testid="add-purpose-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={openNew}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
      </div>
      <div className="space-y-2">
        {purposes.map(e => (
          <div key={e.id} className="flex items-center justify-between bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <IconDisplay name={e.icon} className="w-4 h-4 text-primary" />
              </div>
              <span className="text-sm font-medium">{e.name}</span>
              <Badge variant="outline" className="text-xs">{e.icon}</Badge>
              {!e.active && <Badge variant="secondary" className="text-xs">Inactivo</Badge>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(e.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>{editing ? "Editar Motivo" : "Nuevo Motivo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Nombre</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Icono</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center border border-border">
                  <IconDisplay name={form.icon} className="w-5 h-5 text-primary" />
                </div>
                <Input value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} className="flex-1 bg-background border-border" />
                <Button type="button" size="sm" variant="outline" onClick={() => setIconPicker(true)} data-testid="purpose-icon-picker-btn">
                  <Info className="w-3 h-3 mr-1" /> Iconos
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <IconPicker open={iconPicker} onOpenChange={setIconPicker} value={form.icon} onSelect={v => setForm(p => ({ ...p, icon: v }))} />
    </div>
  );
}

// ==================== CONDITIONS TAB ====================
function ConditionsTab() {
  const [scope, setScope] = useState("global"); // "global" | "sede"
  const [sedes, setSedes] = useState([]);
  const [selectedSedeId, setSelectedSedeId] = useState("");
  const [conditions, setConditions] = useState([]);
  const [globals, setGlobals] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", icon: "info", order: 0 });
  const [iconPicker, setIconPicker] = useState(false);

  useEffect(() => {
    API.get("/sedes?include_inactive=true").then(r => {
      setSedes(r.data);
      if (r.data.length > 0 && !selectedSedeId) setSelectedSedeId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGlobals = useCallback(() => {
    API.get("/conditions?scope=global").then(r => setGlobals(r.data)).catch(() => {});
  }, []);

  const loadSedeData = useCallback(() => {
    if (!selectedSedeId) return;
    Promise.all([
      API.get(`/conditions?sede_id=${selectedSedeId}`),
      API.get(`/sedes/${selectedSedeId}/condition-exclusions`),
    ]).then(([r1, r2]) => {
      setConditions(r1.data);
      setExcluded(r2.data.excluded_condition_ids || []);
    }).catch(() => {});
  }, [selectedSedeId]);

  useEffect(() => { loadGlobals(); }, [loadGlobals]);
  useEffect(() => { if (scope === "sede") loadSedeData(); }, [loadSedeData, scope]);

  const openNew = () => { setEditing(null); setForm({ title: "", description: "", icon: "info", order: 0 }); setDialog(true); };
  const openEdit = (c) => { setEditing(c); setForm({ title: c.title, description: c.description, icon: c.icon, order: c.order }); setDialog(true); };
  const save = async () => {
    const target_sede_id = scope === "global" ? null : selectedSedeId;
    try {
      if (editing) await API.put(`/conditions/${editing.id}`, { ...form, sede_id: editing.sede_id ?? target_sede_id });
      else await API.post("/conditions", { ...form, sede_id: target_sede_id });
      toast.success("Guardado"); setDialog(false);
      loadGlobals();
      if (scope === "sede") loadSedeData();
    } catch { toast.error("Error"); }
  };
  const remove = async (id) => {
    try {
      await API.delete(`/conditions/${id}`);
      toast.success("Eliminado");
      loadGlobals();
      if (scope === "sede") loadSedeData();
    } catch { toast.error("Error"); }
  };

  const toggleExclude = async (condId) => {
    const next = excluded.includes(condId) ? excluded.filter(x => x !== condId) : [...excluded, condId];
    try {
      await API.put(`/sedes/${selectedSedeId}/condition-exclusions`, { excluded_condition_ids: next });
      setExcluded(next);
      loadSedeData();
      toast.success(excluded.includes(condId) ? "Inclu\u00edda en esta sede" : "Exclu\u00edda en esta sede");
    } catch { toast.error("Error"); }
  };

  const visibleList = scope === "global" ? globals : conditions;

  return (
    <div data-testid="conditions-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Condiciones de Reserva</h2>
        <Button data-testid="add-condition-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={openNew} disabled={scope === "sede" && !selectedSedeId}>
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant={scope === "global" ? "default" : "outline"} className={scope === "global" ? "bg-primary text-primary-foreground" : ""} onClick={() => setScope("global")} data-testid="conditions-scope-global">
          <Globe className="w-3 h-3 mr-1" /> Globales
        </Button>
        <Button size="sm" variant={scope === "sede" ? "default" : "outline"} className={scope === "sede" ? "bg-primary text-primary-foreground" : ""} onClick={() => setScope("sede")} data-testid="conditions-scope-sede">
          <MapPin className="w-3 h-3 mr-1" /> Por sede
        </Button>
      </div>
      {scope === "sede" && (
        <div className="mb-4">
          <Label className="text-xs text-muted-foreground mb-1 block">Sede</Label>
          <Select value={selectedSedeId} onValueChange={setSelectedSedeId}>
            <SelectTrigger className="w-64 bg-background border-border" data-testid="conditions-sede-select"><SelectValue placeholder="Selecciona una sede" /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              {sedes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_active === false ? " (Inactiva)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <p className="text-xs text-muted-foreground mb-3">
        {scope === "global"
          ? "Las condiciones globales aplican a todas las sedes por defecto. Cada sede puede excluirlas individualmente."
          : "Vista efectiva de la sede: globales activas (no excluidas) + condiciones espec\u00edficas. Las globales muestran un badge dorado."}
      </p>
      <div className="space-y-2">
        {visibleList.map(c => (
          <div key={c.id} className={`flex items-center justify-between bg-card border border-border rounded-lg p-3 ${c.active === false ? 'opacity-50' : ''}`} data-testid={`condition-row-${c.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <IconDisplay name={c.icon} className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{c.title}</span>
                  {c.is_global && <Badge variant="outline" className="border-primary/30 text-primary text-[10px] py-0">Global</Badge>}
                  {c.active === false && <Badge variant="outline" className="text-muted-foreground text-[10px] py-0">Inactiva</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{(c.description || "").substring(0, 100)}{(c.description || "").length > 100 ? '...' : ''}</p>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0 ml-4 items-center">
              {scope === "sede" && c.is_global && (
                <Button size="sm" variant="ghost" className="text-xs text-orange-400 hover:bg-orange-900/20" onClick={() => toggleExclude(c.id)} data-testid={`exclude-condition-${c.id}`} title="Excluir esta global en esta sede">
                  <XCircle className="w-3 h-3 mr-1" /> Excluir
                </Button>
              )}
              {!c.is_global && (
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-condition-${c.id}`}><Pencil className="w-3 h-3" /></Button>
              )}
              {scope === "global" && (
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-condition-${c.id}`}><Pencil className="w-3 h-3" /></Button>
              )}
              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(c.id)} data-testid={`delete-condition-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
        {visibleList.length === 0 && <p className="text-muted-foreground text-sm py-4">{scope === "global" ? "No hay condiciones globales. Crea una con el botón \"Nueva\"." : "No hay condiciones visibles para esta sede."}</p>}
      </div>
      {scope === "sede" && excluded.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-orange-500/20 bg-orange-500/5">
          <p className="text-xs font-semibold text-orange-400 mb-2">Globales excluidas en esta sede ({excluded.length})</p>
          <div className="space-y-1">
            {globals.filter(g => excluded.includes(g.id)).map(g => (
              <div key={g.id} className="flex items-center justify-between text-xs" data-testid={`excluded-row-${g.id}`}>
                <span className="text-muted-foreground">· {g.title}</span>
                <Button size="sm" variant="ghost" className="text-primary text-xs h-6 px-2" onClick={() => toggleExclude(g.id)} data-testid={`reinclude-condition-${g.id}`}>Reincluir</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>{editing ? "Editar condici\u00f3n" : (scope === "global" ? "Nueva condici\u00f3n global" : "Nueva condici\u00f3n de sede")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Título</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1 bg-background border-border" data-testid="condition-title-input" /></div>
            <div><Label className="text-xs text-muted-foreground">Descripción</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 bg-background border-border" data-testid="condition-description-input" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Icono</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center border border-border">
                  <IconDisplay name={form.icon} className="w-5 h-5 text-primary" />
                </div>
                <Input value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} className="flex-1 bg-background border-border" />
                <Button type="button" size="sm" variant="outline" onClick={() => setIconPicker(true)} data-testid="condition-icon-picker-btn">
                  <Info className="w-3 h-3 mr-1" /> Iconos
                </Button>
              </div>
            </div>
            <div><Label className="text-xs text-muted-foreground">Orden</Label><Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="mt-1 bg-background border-border" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="save-condition-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <IconPicker open={iconPicker} onOpenChange={setIconPicker} value={form.icon} onSelect={v => setForm(p => ({ ...p, icon: v }))} />
    </div>
  );
}

// ==================== BLOCKED DATES TAB ====================
function BlockedDatesTab() {
  const [sedes, setSedes] = useState([]);
  const [selectedSedeId, setSelectedSedeId] = useState("");
  const [dates, setDates] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ date: "", reason: "", start_time: "", end_time: "", is_full_day: true });

  useEffect(() => {
    API.get("/sedes?include_inactive=true").then(r => {
      setSedes(r.data);
      if (r.data.length && !selectedSedeId) setSelectedSedeId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!selectedSedeId) return;
    API.get(`/sedes/${selectedSedeId}/blocked-dates`).then(r => setDates(r.data)).catch(() => setDates([]));
  }, [selectedSedeId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date) { toast.error("Selecciona una fecha"); return; }
    if (!form.is_full_day) {
      if (!form.start_time || !form.end_time) { toast.error("Completa hora de inicio y fin"); return; }
      if (form.start_time >= form.end_time) { toast.error("La hora de inicio debe ser menor a la de fin"); return; }
    }
    try {
      await API.post(`/sedes/${selectedSedeId}/blocked-dates`, {
        date: form.date,
        reason: form.reason,
        start_time: form.is_full_day ? null : form.start_time,
        end_time: form.is_full_day ? null : form.end_time,
      });
      toast.success("Bloqueo creado");
      setDialog(false);
      setForm({ date: "", reason: "", start_time: "", end_time: "", is_full_day: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try { await API.delete(`/sedes/${selectedSedeId}/blocked-dates/${id}`); toast.success("Bloqueo eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-blocked-dates-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Fechas bloqueadas</h2>
        <Button data-testid="add-blocked-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setDialog(true)} disabled={!selectedSedeId}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo bloqueo
        </Button>
      </div>
      <div className="mb-4">
        <Label className="text-xs text-muted-foreground">Sede</Label>
        <Select value={selectedSedeId} onValueChange={setSelectedSedeId}>
          <SelectTrigger className="w-full md:w-72 mt-1 bg-background border-border" data-testid="blocked-sede-select">
            <SelectValue placeholder="Selecciona una sede" />
          </SelectTrigger>
          <SelectContent>
            {sedes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_active === false ? " (Inactiva)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {dates.length === 0 && selectedSedeId && (
          <p className="text-muted-foreground text-sm py-4">No hay fechas bloqueadas para esta sede.</p>
        )}
        {dates.map(d => {
          const isFullDay = !d.start_time && !d.end_time;
          return (
            <div key={d.id} className="flex items-center justify-between bg-card border border-border rounded-lg p-3" data-testid={`blocked-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {isFullDay
                  ? <Ban className="w-4 h-4 text-red-400 flex-shrink-0" />
                  : <Clock className="w-4 h-4 text-primary flex-shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{formatDateStr(d.date)}</span>
                    <Badge className={isFullDay ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-primary/10 text-primary border border-primary/20"} variant="outline">
                      {isFullDay ? "Todo el d\u00eda" : `${d.start_time} – ${d.end_time}`}
                    </Badge>
                  </div>
                  {d.reason && <p className="text-xs text-muted-foreground truncate">{d.reason}</p>}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-900/20" onClick={() => remove(d.id)} data-testid={`delete-blocked-${d.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          );
        })}
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Nuevo bloqueo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-date-input" />
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg border border-border">
              <div>
                <Label className="text-sm">Bloquear todo el d&iacute;a</Label>
                <p className="text-xs text-muted-foreground">Desactiva para bloquear solo un rango horario</p>
              </div>
              <button type="button" data-testid="blocked-fullday-toggle"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${form.is_full_day ? 'bg-primary' : 'bg-secondary'}`}
                onClick={() => setForm(p => ({ ...p, is_full_day: !p.is_full_day }))}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-foreground transition-transform ${form.is_full_day ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {!form.is_full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Hora inicio</Label>
                  <Input type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-start-time" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Hora fin</Label>
                  <Input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} className="mt-1 bg-background border-border" data-testid="blocked-end-time" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Motivo (opcional)</Label>
              <Input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} className="mt-1 bg-background border-border" placeholder="Ej: Mantenimiento, festivo..." data-testid="blocked-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button data-testid="save-blocked-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Bloquear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== RESERVATIONS TAB ====================
function isReservationPast(r) {
  try {
    const t = r.time?.trim().toUpperCase() || "";
    let h = 0, m = 0;
    if (t.includes("AM") || t.includes("PM")) {
      const parts = t.replace(/[AP]M/g, "").trim().split(":");
      h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
      if (t.includes("PM") && h !== 12) h += 12;
      else if (t.includes("AM") && h === 12) h = 0;
    } else {
      const parts = t.split(":"); h = parseInt(parts[0]) || 0; m = parseInt(parts[1]) || 0;
    }
    const [y, mo, d] = (r.date || "").split("-").map(Number);
    const resDate = new Date(y, mo - 1, d, h, m);
    return resDate < new Date();
  } catch { return false; }
}

function ReservationsTab() {
  const [reservations, setReservations] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [filterSede, setFilterSede] = useState("all");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOrigin, setFilterOrigin] = useState("all");
  const [filterNumPeople, setFilterNumPeople] = useState("");
  const [exporting, setExporting] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const buildParams = useCallback(() => {
    const params = {};
    if (filterSede !== "all") params.sede_id = filterSede;
    if (filterDateStart) params.date_start = filterDateStart;
    if (filterDateEnd) params.date_end = filterDateEnd;
    if (filterStatus !== "all") params.status = filterStatus;
    if (filterOrigin !== "all") params.origin = filterOrigin;
    if (filterNumPeople) params.num_people = parseInt(filterNumPeople);
    return params;
  }, [filterSede, filterDateStart, filterDateEnd, filterStatus, filterOrigin, filterNumPeople]);

  const load = useCallback(() => {
    const params = { ...buildParams(), page, per_page: perPage };
    API.get("/reservations", { params }).then(r => {
      const d = r.data;
      setReservations(d.items || d);
      setTotalPages(d.total_pages || 1);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [buildParams, page, perPage]);

  useEffect(() => { API.get("/sedes").then(r => setSedes(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filterSede, filterDateStart, filterDateEnd, filterStatus, filterOrigin, filterNumPeople, perPage]);

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const res = await API.get("/reservations/export", { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reservas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  const handleConfirm = async (id) => {
    try { await API.post(`/reservations/${id}/confirm`); toast.success("Reserva confirmada"); setDetailModal(null); load(); }
    catch { toast.error("Error"); }
  };

  const handleCancel = async (id, reason) => {
    try {
      await API.post(`/reservations/${id}/cancel`, { reason, cancelled_by: "admin" });
      toast.success("Reserva cancelada"); setDetailModal(null); load();
    } catch { toast.error("Error al cancelar"); }
  };

  const statusBadge = (status) => {
    const map = {
      created: { cls: "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-700", text: "Creada" },
      confirmed: { cls: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700", text: "Confirmada" },
      cancelled: { cls: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-700", text: "Cancelada" },
      informational: { cls: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700", text: "Informativo" },
    };
    const s = map[status] || map.created;
    return <Badge variant="outline" className={`font-medium ${s.cls}`}>{s.text}</Badge>;
  };

  const originBadge = (r) => {
    const origin = r.origin || (r.source === "chatbot" ? "chatbot" : "web");
    const map = {
      web: { cls: "bg-[#c9a84c]/20 text-[#c9a84c] border-[#c9a84c]/50", icon: Globe, text: "Web" },
      chatbot: { cls: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40", icon: Bot, text: "Chatbot" },
      instagram: { cls: "bg-orange-500/15 text-orange-400 border-orange-500/40", icon: Instagram, text: "Instagram" },
      whatsapp: { cls: "bg-green-500/15 text-green-400 border-green-500/40", icon: MessageCircle, text: "WhatsApp" },
      pautadigital: { cls: "bg-blue-500/15 text-blue-400 border-blue-500/40", icon: Megaphone, text: "Pauta Digital" },
    };
    const m = map[origin] || map.web;
    const Icon = m.icon;
    return (
      <Badge variant="outline" className={`text-[10px] ${m.cls}`} data-testid={`origin-${origin}-${r.id}`}>
        <Icon className="w-3 h-3 mr-1" /> {m.text}
      </Badge>
    );
  };

  return (
    <div data-testid="reservations-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold">Reservas</h2>
        <Button onClick={downloadExcel} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="export-excel-btn">
          <Download className="w-4 h-4 mr-2" /> {exporting ? "Generando..." : "Descargar Excel"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Sede</Label>
          <Select value={filterSede} onValueChange={setFilterSede}>
            <SelectTrigger className="w-44 bg-background border-border" data-testid="filter-sede"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Todas las sedes</SelectItem>
              {sedes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Estado</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-background border-border" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="created">Creadas</SelectItem>
              <SelectItem value="confirmed">Confirmadas</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
              <SelectItem value="informational">Informativas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Origen</Label>
          <Select value={filterOrigin} onValueChange={setFilterOrigin}>
            <SelectTrigger className="w-40 bg-background border-border" data-testid="filter-origin"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="web">Web</SelectItem>
              <SelectItem value="chatbot">Chatbot</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="pautadigital">Pauta Digital</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">N&ordm; personas</Label>
          <Input type="number" min={1} value={filterNumPeople} onChange={e => setFilterNumPeople(e.target.value)} placeholder="Ej: 4" className="w-28 bg-background border-border" data-testid="filter-num-people" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Desde</Label>
          <Input type="date" value={filterDateStart} onChange={e => setFilterDateStart(e.target.value)} className="w-40 bg-background border-border" data-testid="filter-date-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Hasta</Label>
          <Input type="date" value={filterDateEnd} onChange={e => { if (filterDateStart && e.target.value < filterDateStart) { toast.error("La fecha final no puede ser anterior a la inicial"); return; } setFilterDateEnd(e.target.value); }} className="w-40 bg-background border-border" data-testid="filter-date-end" />
        </div>
        {(filterDateStart || filterDateEnd || filterOrigin !== "all" || filterNumPeople || filterSede !== "all" || filterStatus !== "all") && (
          <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={() => { setFilterDateStart(""); setFilterDateEnd(""); setFilterOrigin("all"); setFilterNumPeople(""); setFilterSede("all"); setFilterStatus("all"); }} data-testid="clear-filters-btn">Limpiar filtros</Button>
        )}
      </div>
      <div className="w-full overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">C&oacute;digo</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Nombre</TableHead>
              <TableHead className="text-muted-foreground">Sede</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Origen</TableHead>
              <TableHead className="text-muted-foreground">Fecha</TableHead>
              <TableHead className="text-muted-foreground">Hora</TableHead>
              <TableHead className="text-muted-foreground">Estado</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservations.map(r => {
              const past = isReservationPast(r);
              return (
                <TableRow key={r.id} data-testid={`reservation-row-${r.id}`} className={`border-border cursor-pointer hover:bg-muted/50 ${past ? 'opacity-60' : ''}`} onClick={() => setDetailModal(r)}>
                  <TableCell className="text-primary font-medium text-xs">{r.reservation_code}</TableCell>
                  <TableCell className="text-sm hidden md:table-cell">{r.full_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.sede_name}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {originBadge(r)}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateStr(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.time}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-xs h-7 w-7 p-0" onClick={() => setDetailModal(r)} title="Ver detalle" data-testid={`view-reservation-${r.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10 h-7 w-7 p-0" onClick={() => setDeleteConfirm(r.id)} title="Eliminar" data-testid={`delete-reservation-${r.id}`}><Trash className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {reservations.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No hay reservas</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 text-sm">
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground">{total} reservas</span>
          <Select value={String(perPage)} onValueChange={v => setPerPage(Number(v))}>
            <SelectTrigger className="w-20 h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">por p&aacute;gina</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">Anterior</Button>
          <span className="text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">Siguiente</Button>
        </div>
      </div>
      <ReservationDetailModal
        reservation={detailModal}
        onClose={() => setDetailModal(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onDelete={(id) => setDeleteConfirm(id)}
        isAdmin={true}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash className="w-5 h-5 text-red-400" /> Eliminar Reserva</DialogTitle>
            <DialogDescription>Esta acci&oacute;n es permanente y no se puede deshacer. La reserva ser&aacute; eliminada del sistema.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={async () => {
              try { await API.delete(`/reservations/${deleteConfirm}`); toast.success("Reserva eliminada"); setDeleteConfirm(null); setDetailModal(null); load(); }
              catch { toast.error("Error al eliminar"); }
            }}>Eliminar permanentemente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== RESERVATION DETAIL MODAL ====================
function formatCreatedAt(isoStr) {
  if (!isoStr) return "-";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return isoStr; }
}

function ReservationDetailModal({ reservation, onClose, onConfirm, onCancel, onDelete, isAdmin }) {
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  if (!reservation) return null;
  const r = reservation;
  const past = isReservationPast(r);
  const statusMap = { created: "Creada", confirmed: "Confirmada", cancelled: "Cancelada", informational: "Informativo" };
  const statusCls = {
    created: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    confirmed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    cancelled: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    informational: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  };

  const handleCancelSubmit = () => {
    if (!cancelReason.trim()) { toast.error("Ingresa un motivo de cancelaci\u00f3n"); return; }
    onCancel(r.id, cancelReason);
    setShowCancelForm(false);
    setCancelReason("");
  };

  const handleClose = () => {
    setShowCancelForm(false);
    setCancelReason("");
    onClose();
  };

  return (
    <Dialog open={!!reservation} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border sm:max-w-md w-full max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto p-0 sm:p-6 rounded-none sm:rounded-lg gap-0 sm:gap-4" data-testid="reservation-detail-modal">
        <DialogHeader className="p-4 sm:p-0 border-b sm:border-0 border-border sticky top-0 bg-card z-10">
          <DialogTitle className="text-lg">Detalle de Reserva</DialogTitle>
          <DialogDescription>C&oacute;digo: {r.reservation_code}</DialogDescription>
        </DialogHeader>
        <div className="p-4 sm:p-0 space-y-1">
          <div className="flex justify-between py-2 border-b border-border/30">
            <span className="text-sm text-muted-foreground">Estado</span>
            <span className={`text-sm font-semibold px-2 py-0.5 rounded-md ${statusCls[r.status] || ''}`}>{statusMap[r.status] || r.status}</span>
          </div>
          {[
            ["Nombre", r.full_name],
            ["Email", r.email],
            ["Tel\u00e9fono", r.phone],
            ["Sede", r.sede_name],
            ["Fecha", formatDateStr(r.date)],
            ["Hora", r.time],
            ["Personas", r.num_people],
            ["Motivo de reserva", r.reservation_purpose],
            ...(r.zone_type ? [["Tipo de zona", r.zone_type]] : []),
            ...(r.comments ? [["Comentarios", r.comments]] : []),
            ...(r.cancellation_reason ? [["Motivo cancelaci\u00f3n", r.cancellation_reason]] : []),
            ["Fecha de creaci\u00f3n", formatCreatedAt(r.created_at)],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between py-2 border-b border-border/30 last:border-0">
              <span className="text-sm text-muted-foreground">{label}</span>
              <span className="text-sm font-medium text-right max-w-[60%] break-words">{value}</span>
            </div>
          ))}
          {showCancelForm && (
            <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg space-y-3">
              <p className="text-sm font-medium text-red-500 flex items-center gap-2"><XCircle className="w-4 h-4" /> Cancelar reserva</p>
              <Textarea
                data-testid="detail-cancel-reason"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="bg-background border-border"
                placeholder="Ingresa el motivo de la cancelaci&oacute;n..."
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => { setShowCancelForm(false); setCancelReason(""); }}>Volver</Button>
                <Button size="sm" variant="destructive" onClick={handleCancelSubmit} disabled={!cancelReason.trim()} data-testid="confirm-cancel-btn">Confirmar cancelaci&oacute;n</Button>
              </div>
            </div>
          )}
        </div>
        {!showCancelForm && (
          <div className="p-4 sm:p-0 border-t sm:border-0 border-border flex flex-wrap gap-2 justify-end sticky bottom-0 bg-card">
            {r.status === "informational" && (
              <span className="text-xs text-amber-500 mr-auto self-center flex items-center gap-1" data-testid="informational-note">
                <Info className="w-3.5 h-3.5" /> Reserva informativa (corporativa): no admite acciones.
              </span>
            )}
            {!past && r.status === "created" && onConfirm && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onConfirm(r.id)} data-testid="detail-confirm-btn">
                <Check className="w-4 h-4 mr-1" /> Confirmar
              </Button>
            )}
            {!past && r.status !== "cancelled" && r.status !== "informational" && onCancel && (
              <Button size="sm" variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => setShowCancelForm(true)} data-testid="detail-cancel-btn">
                <XCircle className="w-4 h-4 mr-1" /> Cancelar reserva
              </Button>
            )}
            {isAdmin && onDelete && (
              <Button size="sm" variant="outline" className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10" onClick={() => onDelete(r.id)} data-testid="detail-delete-btn">
                <Trash className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleClose}>Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================== USERS TAB ====================
function ReceiveReservationEmailsField({ role, value, onChange, testIdPrefix }) {
  const disabled = role !== "sede" && role !== "admin";
  const helper = role === "sede"
    ? "Recibir\u00e1s un correo cada vez que haya una nueva reserva en esta sede."
    : role === "admin"
      ? "Recibir\u00e1s un correo por CADA nueva reserva en todas las sedes de la plataforma."
      : "Esta opci\u00f3n solo est\u00e1 disponible para roles de sede y administrador.";
  return (
    <div className="pt-1" data-testid={`${testIdPrefix}-notify-field`}>
      <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background/50 p-3">
        <div className="min-w-0">
          <Label htmlFor={`${testIdPrefix}-notify-switch`} className="text-sm text-foreground font-medium cursor-pointer">
            Recibir correos de reservas
          </Label>
          <p className="text-xs text-muted-foreground mt-1 leading-snug">{helper}</p>
        </div>
        <Switch
          id={`${testIdPrefix}-notify-switch`}
          data-testid={`${testIdPrefix}-notify-switch`}
          checked={!disabled && !!value}
          disabled={disabled}
          onCheckedChange={onChange}
        />
      </div>
    </div>
  );
}

function UsersTab() {
  const [allUsers, setAllUsers] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "sede", sede_id: "", sede_ids: [], receive_reservation_emails: true });
  const [editForm, setEditForm] = useState({ name: "", role: "sede", sede_ids: [], receive_reservation_emails: false });
  const { user: currentUser, impersonate } = useAuth();
  const [roleFilter, setRoleFilter] = useState("staff");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const load = useCallback(() => { API.get("/admin/users").then(r => setAllUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
    API.get("/sedes").then(r => setSedes(r.data)).catch(() => {});
  }, [load]);

  const filteredUsers = useMemo(() => {
    if (roleFilter === "all") return allUsers;
    if (roleFilter === "staff") return allUsers.filter(u => u.role !== "client");
    return allUsers.filter(u => u.role === roleFilter);
  }, [allUsers, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
  const paginatedUsers = filteredUsers.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [roleFilter, perPage]);

  const save = async () => {
    try {
      await API.post("/admin/users", { ...form, sede_ids: form.sede_ids });
      toast.success("Usuario creado"); setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const saveEdit = async () => {
    try {
      await API.put(`/admin/users/${editDialog.id}`, editForm);
      toast.success("Usuario actualizado"); setEditDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    try { await API.delete(`/admin/users/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const user = await impersonate(userId);
      if (user.role === "sede") window.location.href = "/sede";
      else if (user.role === "client") window.location.href = "/mis-reservas";
      else window.location.href = "/";
    } catch { toast.error("Error al cambiar de usuario"); }
  };

  const toggleSede = (sedeId, obj, setter) => {
    setter(prev => {
      const updated = { ...prev };
      const list = [...(updated.sede_ids || [])];
      if (list.includes(sedeId)) updated.sede_ids = list.filter(s => s !== sedeId);
      else updated.sede_ids = [...list, sedeId];
      return updated;
    });
  };

  const getSedeNames = (u) => {
    const ids = u.sede_ids || (u.sede_id ? [u.sede_id] : []);
    return ids.map(id => sedes.find(s => s.id === id)?.name || "?").join(", ");
  };

  return (
    <div data-testid="users-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold">Usuarios</h2>
        <Button data-testid="add-user-btn" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { setForm({ email: "", name: "", role: "sede", sede_id: "", sede_ids: [], receive_reservation_emails: true }); setDialog(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo
        </Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-44 bg-background border-border" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="staff">Admin y Sede</SelectItem>
            <SelectItem value="admin">Solo Admin</SelectItem>
            <SelectItem value="sede">Solo Sede</SelectItem>
            <SelectItem value="client">Solo Clientes</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filteredUsers.length} usuarios</span>
      </div>
      <div className="w-full overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">Email</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Nombre</TableHead>
              <TableHead className="text-muted-foreground">Rol</TableHead>
              <TableHead className="text-muted-foreground hidden md:table-cell">Sedes</TableHead>
              <TableHead className="text-muted-foreground">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedUsers.map(u => (
              <TableRow key={u.id} className="border-border">
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm hidden md:table-cell">{u.name}</TableCell>
                <TableCell><Badge variant="outline" className={u.role === 'admin' ? 'border-primary text-primary' : ''}>{u.role}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate hidden md:table-cell">{getSedeNames(u) || "-"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {u.role !== "admin" && (
                      <Button size="sm" variant="ghost" className="text-primary text-xs" onClick={() => handleImpersonate(u.id)} data-testid={`impersonate-${u.id}`}>
                        <LogIn className="w-3 h-3 mr-1" /> Ingresar
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setEditDialog(u); setEditForm({ name: u.name, role: u.role, sede_ids: u.sede_ids || (u.sede_id ? [u.sede_id] : []), receive_reservation_emails: !!u.receive_reservation_emails }); }} data-testid={`edit-user-${u.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Editar
                    </Button>
                    {currentUser?.id !== u.id && (
                      <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginatedUsers.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No hay usuarios</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 text-sm">
        <div className="flex items-center gap-3">
          <Select value={String(perPage)} onValueChange={v => setPerPage(Number(v))}>
            <SelectTrigger className="w-20 h-8 bg-background border-border text-xs" data-testid="users-per-page"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-muted-foreground text-xs">por p&aacute;gina</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </div>
      </div>
      {/* Create User Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Nuevo Usuario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Nombre</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Rol</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v, receive_reservation_emails: v === "sede" }))}>
                <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="sede">Sede</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "sede" && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Sedes asignadas</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {sedes.map(s => (
                    <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary cursor-pointer">
                      <input type="checkbox" checked={form.sede_ids?.includes(s.id)} onChange={() => toggleSede(s.id, form, setForm)} className="rounded border-border" />
                      <span className="text-sm">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <ReceiveReservationEmailsField
              testIdPrefix="create-user"
              role={form.role}
              value={!!form.receive_reservation_emails}
              onChange={(v) => setForm(p => ({ ...p, receive_reservation_emails: v }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={save}>Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>{editDialog?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Nombre</Label><Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Rol</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="sede">Sede</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "sede" && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Sedes asignadas</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                  {sedes.map(s => (
                    <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-secondary cursor-pointer">
                      <input type="checkbox" checked={editForm.sede_ids?.includes(s.id)} onChange={() => toggleSede(s.id, editForm, setEditForm)} className="rounded border-border" />
                      <span className="text-sm">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <ReceiveReservationEmailsField
              testIdPrefix="edit-user"
              role={editForm.role}
              value={!!editForm.receive_reservation_emails}
              onChange={(v) => setEditForm(p => ({ ...p, receive_reservation_emails: v }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancelar</Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={saveEdit}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ============ RECURSOS TAB ============
const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const formatUsd = (v) => `US$${Number(v || 0).toFixed(4).replace(/\.?0+$/, "") || "0"}`;
const formatUsdShort = (v) => `US$${Number(v || 0).toFixed(2)}`;
const formatIntEs = (n) => new Intl.NumberFormat("es-CO").format(Number(n || 0));

function TierCard({ tier, isCurrent }) {
  return (
    <Card data-testid={`resources-tier-card-${tier.tier}`} className="overflow-hidden">
      <div className="h-1.5 w-full" style={{ backgroundColor: tier.color }} />
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
            >
              <Cpu className="w-4 h-4" />
            </div>
            <div className="font-heading font-bold text-base">{tier.tier}</div>
          </div>
          {isCurrent && (
            <Badge
              data-testid={`resources-tier-in-use-${tier.tier}`}
              className="text-[10px] uppercase tracking-wide"
              style={{ backgroundColor: tier.color, color: "#0f0f1a" }}
            >
              En uso
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground min-h-[32px]">{tier.description}</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-muted/40 rounded-md p-2">
            <div className="text-muted-foreground">vCPU</div>
            <div className="font-semibold text-sm">{tier.vcpu}</div>
          </div>
          <div className="bg-muted/40 rounded-md p-2">
            <div className="text-muted-foreground">RAM</div>
            <div className="font-semibold text-sm">{tier.ram_mb} MB</div>
          </div>
          <div className="col-span-2 bg-muted/40 rounded-md p-2">
            <div className="text-muted-foreground">Emails gratis / mes</div>
            <div className="font-semibold text-sm">{formatIntEs(tier.free_emails_per_month)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResourcesTab() {
  const [tiersData, setTiersData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState({ items: [], platform_tier: "STARTER", free_quota: 0 });
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [filterYear, setFilterYear] = useState(String(now.getFullYear()));
  const [filterMonth, setFilterMonth] = useState("all");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear && filterYear !== "all") params.set("year", filterYear);
      if (filterMonth && filterMonth !== "all") params.set("month", filterMonth);
      const [t, s, u] = await Promise.all([
        API.get("/admin/resources/tiers"),
        API.get("/admin/resources/email-usage/summary"),
        API.get(`/admin/resources/email-usage?${params.toString()}`),
      ]);
      setTiersData(t.data);
      setSummary(s.data);
      setUsage(u.data);
    } catch (e) {
      toast.error("Error al cargar los recursos");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [String(y), String(y - 1), String(y - 2)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const platformTier = summary?.platform_tier || "STARTER";
  const current = summary?.current_month;
  const totalAllTime = summary?.total_all_time ?? 0;
  const usagePct = current && current.free_quota
    ? Math.min(100, Math.round((current.emails_sent / current.free_quota) * 100))
    : 0;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="resources-tab">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Recursos</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cat&aacute;logo de planes de infraestructura y seguimiento del env&iacute;o de emails de la plataforma.
        </p>
      </div>

      {/* Tier cards */}
      <div>
        <h2 className="text-base font-heading font-semibold text-foreground mb-3">Planes disponibles</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(tiersData?.tiers || []).map(t => (
            <TierCard key={t.tier} tier={t} isCurrent={t.tier === platformTier} />
          ))}
          {loading && !tiersData && (
            <div className="col-span-full text-sm text-muted-foreground">Cargando planes&hellip;</div>
          )}
        </div>
      </div>

      {/* Email pricing explainer */}
      <Card data-testid="resources-pricing-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-primary" />
            <h2 className="text-base font-heading font-semibold text-foreground">Precio de los emails</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Los primeros emails de cada mes est&aacute;n incluidos seg&uacute;n el plan. Los excedentes se
            facturan al precio indicado a continuaci&oacute;n.
          </p>
          <div className="inline-flex bg-primary/10 border border-primary/20 rounded-md px-4 py-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-primary">Precio final al cliente</div>
              <div className="text-lg font-semibold mt-1 text-primary whitespace-nowrap">
                {formatUsdShort(tiersData?.pricing?.client_price_display_usd)}
                <span className="text-xs font-normal"> / 1.000 emails</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform counters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card data-testid="resources-counter-current">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
              <Mail className="w-3.5 h-3.5" />
              Emails este mes
            </div>
            <div className="text-3xl font-heading font-bold mt-2">{formatIntEs(current?.emails_sent)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              de {formatIntEs(current?.free_quota)} incluidos &middot; plan {platformTier}
            </div>
            <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${usagePct}%` }}
              />
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{usagePct}% de la cuota mensual</div>
          </CardContent>
        </Card>
        <Card data-testid="resources-counter-excess">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
              <TrendingUp className="w-3.5 h-3.5" />
              Excedente del mes
            </div>
            <div className="text-3xl font-heading font-bold mt-2">
              {formatIntEs(current?.excess)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Costo estimado: <span className="font-semibold text-foreground">{formatUsd(current?.overage_cost_usd)}</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="resources-counter-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
              <BarChart3 className="w-3.5 h-3.5" />
              Total hist&oacute;rico
            </div>
            <div className="text-3xl font-heading font-bold mt-2">{formatIntEs(totalAllTime)}</div>
            <div className="text-xs text-muted-foreground mt-1">Emails enviados desde el inicio</div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly usage table */}
      <Card data-testid="resources-usage-card">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-heading font-semibold text-foreground">Uso mensual de emails</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Historial de env&iacute;os por mes con c&aacute;lculo de excedente y costo estimado.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-[110px] h-9" data-testid="resources-filter-year">
                  <SelectValue placeholder="A&ntilde;o" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-[140px] h-9" data-testid="resources-filter-month">
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los meses</SelectItem>
                  {MONTHS_ES.map((name, idx) => (
                    <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Periodo</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Emails enviados</TableHead>
                  <TableHead className="text-right">Cuota gratis</TableHead>
                  <TableHead className="text-right">Excedente</TableHead>
                  <TableHead className="text-right">Costo excedente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-6">Cargando&hellip;</TableCell></TableRow>
                )}
                {!loading && usage.items.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-6">Sin registros para el filtro seleccionado</TableCell></TableRow>
                )}
                {!loading && usage.items.map(row => (
                  <TableRow key={`${row.year}-${row.month}`} data-testid={`resources-usage-row-${row.year}-${row.month}`}>
                    <TableCell className="font-medium">
                      {MONTHS_ES[(row.month || 1) - 1]} {row.year}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.tier}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatIntEs(row.emails_sent)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{formatIntEs(row.free_quota)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.excess > 0
                        ? <span className="text-amber-600 dark:text-amber-400 font-semibold">{formatIntEs(row.excess)}</span>
                        : <span className="text-muted-foreground">0</span>
                      }
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {row.overage_cost_usd > 0 ? formatUsd(row.overage_cost_usd) : <span className="text-muted-foreground font-normal">US$0</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
